import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { translations } from './translations';

// Import Shadcn components
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Calendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { toast } from './hooks/use-toast';
import { Toaster } from './components/ui/toaster';

// Icons from lucide-react
import { CalendarDays, Clock, Users, MapPin, Phone, Mail, Music, Train, Instagram, Cake, ShieldCheck, PartyPopper } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format date string YYYY-MM-DD to DD/MM/YYYY without timezone conversion
const formatDateString = (dateString) => {
  if (!dateString) return '';
  // Direct string manipulation - no Date object to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${day}/${month}/${year}`;
};

// Format date for detailed display (e.g., "lundi 30 décembre 2025")
const formatDateStringLong = (dateString) => {
  if (!dateString) return '';
  // Use the Date constructor with explicit UTC to avoid timezone shift
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString('fr-FR', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC' 
  });
};

// Language Context
const LanguageContext = createContext();

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('popbox-language') || 'fr';
  });

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('popbox-language', lang);
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.fr[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Rooms data matching backend - NOMS EN MAJUSCULES SANS ACCENTS
const ROOMS = {
  "amsterdam": { name: "AMSTERDAM", capacity: 2, streetView: true, description: "Vue sur vitrine (on vous voit à l'œuvre 🤩🎤)" },
  "tokyo": { name: "TOKYO", capacity: 6, streetView: false, description: "Ambiance authentique" },
  "shanghai": { name: "SHANGHAI", capacity: 2, streetView: false, description: "Intimité et style" },
  "seoul": { name: "SEOUL", capacity: 5, streetView: false, description: "Parfait pour petits groupes" },
  "pekin": { name: "PEKIN", capacity: 10, streetView: false, description: "Grand box pour groupes" },
  "busan": { name: "BUSAN", capacity: 3, streetView: false, description: "Cosy et moderne" },
  "kyoto": { name: "KYOTO", capacity: 10, streetView: false, description: "Notre plus grand box" }
};

// Navigation Component - VERSION SIMPLIFIÉE MOBILE
const Navigation = ({ onBookingClick, onContactClick, onBirthdayClick }) => {
  const { t, language, changeLanguage } = useLanguage();
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-purple-500/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo Section */}
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="w-12 h-12 md:w-16 md:h-16 relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_0037442e-f8c4-410f-88d1-489e1b7cc4b8/artifacts/2uovxfyb_0D5E3D07-3DAC-4798-9E5E-400BC0F0A727.webp" 
                alt="Pop Box Karaoke Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-base md:text-2xl font-black bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent orbitron-font">
              POP BOX KARAOKE
            </span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('fr')}
                className={`p-2 ${language === 'fr' ? 'bg-pink-500/20 border border-pink-500/50' : 'hover:bg-gray-700/50'}`}
              >
                🇫🇷
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('en')}
                className={`p-2 ${language === 'en' ? 'bg-cyan-500/20 border border-cyan-500/50' : 'hover:bg-gray-700/50'}`}
              >
                🇬🇧
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('zh')}
                className={`p-2 ${language === 'zh' ? 'bg-pink-500/20 border border-pink-500/50' : 'hover:bg-gray-700/50'}`}
              >
                🇨🇳
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('it')}
                className={`p-2 ${language === 'it' ? 'bg-pink-500/20 border border-pink-500/50' : 'hover:bg-gray-700/50'}`}
              >
                🇮🇹
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('es')}
                className={`p-2 ${language === 'es' ? 'bg-pink-500/20 border border-pink-500/50' : 'hover:bg-gray-700/50'}`}
              >
                🇪🇸
              </Button>
            </div>
            <Button 
              variant="ghost" 
              className="text-white hover:text-pink-400 hover:bg-pink-400/10 orbitron-font font-semibold"
              onClick={onBookingClick}
            >
              {t('nav_reserve')}
            </Button>
            <Button 
              variant="ghost" 
              className="text-white hover:text-purple-400 hover:bg-purple-400/10 orbitron-font font-semibold"
              onClick={onBirthdayClick}
              data-testid="nav-birthday-desktop"
            >
              ANNIVERSAIRE
            </Button>
            <Button 
              variant="ghost" 
              className="text-white hover:text-cyan-400 hover:bg-cyan-400/10 orbitron-font font-semibold"
              onClick={onContactClick}
            >
              {t('nav_contact')}
            </Button>
            <a 
              href="https://www.instagram.com/popboxnice" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-pink-400 transition-colors p-2"
              data-testid="nav-instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>

          {/* MOBILE MENU - TOUS BOUTONS VISIBLES */}
          <div className="md:hidden flex items-center flex-wrap gap-1">
            {/* Drapeaux */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage('fr')}
              className={`p-1 ${language === 'fr' ? 'bg-pink-500/30' : ''}`}
            >
              🇫🇷
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage('en')}
              className={`p-1 ${language === 'en' ? 'bg-cyan-500/30' : ''}`}
            >
              🇬🇧
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage('zh')}
              className={`p-1 ${language === 'zh' ? 'bg-pink-500/30' : ''}`}
            >
              🇨🇳
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage('it')}
              className={`p-1 ${language === 'it' ? 'bg-pink-500/30' : ''}`}
            >
              🇮🇹
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage('es')}
              className={`p-1 ${language === 'es' ? 'bg-pink-500/30' : ''}`}
            >
              🇪🇸
            </Button>
            
            {/* Bouton RÉSERVER - MOBILE */}
            <Button 
              size="sm"
              className="bg-pink-500 text-white hover:bg-pink-600 orbitron-font font-bold text-xs px-2 py-1"
              onClick={onBookingClick}
            >
              RÉSERVER
            </Button>

            {/* Bouton ANNIVERSAIRE - MOBILE */}
            <Button 
              size="sm"
              className="bg-purple-500 text-white hover:bg-purple-600 orbitron-font font-bold text-xs px-2 py-1"
              onClick={onBirthdayClick}
              data-testid="nav-birthday-mobile"
            >
              ANNIV
            </Button>
            
            {/* Bouton CONTACT - MOBILE */}
            <Button 
              size="sm"
              className="bg-cyan-500 text-white hover:bg-cyan-600 orbitron-font font-bold text-xs px-2 py-1"
              onClick={onContactClick}
            >
              CONTACT
            </Button>
            <a 
              href="https://www.instagram.com/popboxnice" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-pink-400 transition-colors p-1"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onBookingClick }) => {
  const { t } = useLanguage();
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900">
        <div className="absolute inset-0 opacity-30">
          <div className="w-full h-full bg-gradient-to-r from-pink-500/10 via-transparent to-cyan-500/10 animate-pulse"></div>
        </div>
      </div>

      {/* Neon effects */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-pink-500/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-cyan-400/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-purple-500/30 rounded-full blur-3xl animate-pulse"></div>

      <div className="relative z-10 text-center max-w-4xl mx-auto px-4 py-8">
        {/* Logo effet avec le vrai logo */}
        <div className="mb-6 flex justify-center items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_0037442e-f8c4-410f-88d1-489e1b7cc4b8/artifacts/2uovxfyb_0D5E3D07-3DAC-4798-9E5E-400BC0F0A727.webp" 
            alt="Pop Box Karaoke"
            className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain drop-shadow-2xl"
          />
        </div>

        {/* Photo de la boutique */}
        <div className="mb-8 mx-auto max-w-3xl rounded-2xl overflow-hidden shadow-2xl shadow-pink-500/20 border border-pink-500/20">
          <img 
            src="https://customer-assets.emergentagent.com/job_d25da3b9-d289-4726-8c75-9e06eeaa08bf/artifacts/57y07brr_IMG_4884.jpeg" 
            alt="Pop Box Karaoke - Accueil de la boutique"
            className="w-full h-48 sm:h-64 md:h-80 object-cover"
            data-testid="hero-boutique-photo"
          />
        </div>
          
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black mb-4 relative orbitron-font">
          <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-pulse">
            POP BOX
          </span>
          <div className="absolute -inset-1 bg-gradient-to-r from-pink-600/50 to-cyan-600/50 blur-lg -z-10 animate-pulse"></div>
        </h1>
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 orbitron-font">
          <span className="text-pink-400">K</span>
          <span className="text-purple-400">A</span>
          <span className="text-cyan-400">R</span>
          <span className="text-pink-400">A</span>
          <span className="text-purple-400">O</span>
          <span className="text-cyan-400">K</span>
          <span className="text-pink-400">E</span>
        </h2>

        <p className="text-lg md:text-xl lg:text-2xl text-gray-300 mb-6 md:mb-8 max-w-2xl mx-auto noto-sans-font px-4">
          <strong>{t('hero_subtitle')}</strong>
          <br />
          <span className="text-pink-400 font-semibold">{t('hero_rooms')}</span> · 
          <span className="text-cyan-400 font-semibold"> {t('hero_booking')}</span>
        </p>

        <div className="space-y-3 md:space-y-0 md:space-x-4 flex flex-col md:flex-row items-center justify-center px-4">
          <Button 
            size="lg"
            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-pink-500/25 orbitron-font w-full md:w-auto"
            onClick={onBookingClick}
          >
            {t('hero_cta')}
          </Button>
          <Button 
            size="lg"
            variant="outline"
            className="border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-gray-900 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold transition-all duration-300 orbitron-font w-full md:w-auto"
          >
            {t('hero_discover')}
          </Button>
        </div>

        {/* Info badges */}
        <div className="flex flex-wrap justify-center gap-4 mt-12">
          <Badge className="bg-pink-500/20 text-pink-300 border-pink-500/30 px-4 py-2 text-sm noto-sans-font">
            <Clock className="w-4 h-4 mr-2" />
            {t('badge_open')}
          </Badge>
          <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 px-4 py-2 text-sm noto-sans-font">
            <Users className="w-4 h-4 mr-2" />
            {t('badge_capacity')}
          </Badge>
          <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 px-4 py-2 text-sm noto-sans-font">
            <MapPin className="w-4 h-4 mr-2" />
            {t('badge_location')}
          </Badge>
        </div>
      </div>
    </section>
  );
};

// Rooms Section avec fond animé et boutons RÉSERVER
const RoomsSection = ({ onBookingClick }) => {
  const { t } = useLanguage();
  
  const handleRoomBooking = (roomId) => {
    onBookingClick(roomId);
  };
  
  return (
    <section id="rooms" className="py-20 px-4 relative overflow-hidden">
      {/* Fond animé multicolore avec faisceaux */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/70 to-gray-900">
        {/* Faisceaux lumineux animés */}
        <div className="absolute inset-0">
          {/* Faisceau 1 - Rose */}
          <div className="absolute top-0 left-1/4 w-2 h-full bg-gradient-to-b from-pink-500/40 via-pink-400/20 to-transparent transform -skew-x-12 beam-animation"></div>
          
          {/* Faisceau 2 - Cyan */}
          <div className="absolute top-0 right-1/3 w-3 h-full bg-gradient-to-b from-cyan-400/30 via-cyan-300/15 to-transparent transform skew-x-6 beam-animation" style={{animationDelay: '1s'}}></div>
          
          {/* Faisceau 3 - Violet */}
          <div className="absolute top-0 left-1/2 w-1 h-full bg-gradient-to-b from-purple-500/50 via-purple-400/25 to-transparent transform -skew-x-3 beam-animation" style={{animationDelay: '0.5s'}}></div>
          
          {/* Particules scintillantes */}
          <div className="absolute top-10 left-10 w-2 h-2 bg-pink-400 rounded-full sparkle"></div>
          <div className="absolute top-32 right-20 w-1 h-1 bg-cyan-300 rounded-full sparkle" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-40 left-1/3 w-3 h-3 bg-purple-400 rounded-full sparkle" style={{animationDelay: '0.3s'}}></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 orbitron-font">
            <span className="bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent">
              {t('rooms_title')}
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto noto-sans-font">
            {t('rooms_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Object.entries(ROOMS)
            .sort(([, a], [, b]) => a.capacity - b.capacity) // Tri par capacité croissante
            .map(([roomId, room]) => (
            <Card 
              key={roomId}
              className="bg-gray-800/80 border-purple-500/30 hover:border-pink-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/20 backdrop-blur-sm"
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-xl font-bold flex items-center justify-between orbitron-font">
                  {room.name}
                  {room.streetView && (
                    <Badge className="bg-cyan-500/20 text-cyan-300 text-xs noto-sans-font">
                      {t('room_street_view')}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm mb-4 noto-sans-font">{room.description}</p>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-pink-400" />
                    <span className="text-pink-400 font-semibold noto-sans-font">
                      {room.capacity} {room.capacity === 1 ? t('room_person') : t('room_people')}
                    </span>
                  </div>
                  <Badge 
                    variant="outline" 
                    className="border-purple-400/50 text-purple-300 noto-sans-font"
                  >
                    {t('room_session')}
                  </Badge>
                </div>
                
                {/* Bouton Réserver pour chaque box - VISIBLE */}
                <Button
                  onClick={() => handleRoomBooking(roomId)}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-2 transition-all duration-300 orbitron-font"
                >
                  RÉSERVER
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const { t } = useLanguage();
  
  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-purple-900/20">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 orbitron-font">
          <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            {t('pricing_title')}
          </span>
        </h2>
        <p className="text-xl text-gray-300 mb-12 noto-sans-font">
          {t('pricing_subtitle')}
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <Card className="bg-gradient-to-br from-purple-900/50 to-pink-900/30 border-purple-500/50">
            <CardHeader>
              <CardTitle className="text-white text-2xl orbitron-font">
                <Clock className="w-6 h-6 text-purple-400 mb-2" />
                {t('pricing_normal')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-purple-400 mb-2 orbitron-font">8€</div>
              <p className="text-gray-300 mb-4 noto-sans-font">par personne / heure</p>
              <p className="text-sm text-gray-400 noto-sans-font">
                {t('pricing_normal_hours')}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-pink-900/50 to-cyan-900/30 border-pink-500/50">
            <CardHeader>
              <CardTitle className="text-white text-2xl orbitron-font">
                <Music className="w-6 h-6 text-pink-400 mb-2" />
                {t('pricing_peak')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-pink-400 mb-2 orbitron-font">10€</div>
              <p className="text-gray-300 mb-4 noto-sans-font">par personne / heure</p>
              <p className="text-sm text-gray-400 noto-sans-font">
                {t('pricing_peak_hours')}
                <br />
                <span className="text-pink-400">{t('pricing_peak_subtitle')}</span>
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 p-6 bg-gray-800/50 rounded-lg border border-cyan-500/30">
          <h3 className="text-xl font-semibold text-cyan-400 mb-3 orbitron-font">{t('pricing_opening_hours')}</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-300 noto-sans-font">
            <div>
              <p className="font-medium text-white">{t('pricing_week_days')}</p>
              <p>17h00 - 00h00</p>
            </div>
            <div>
              <p className="font-medium text-white">Mercredi</p>
              <p>13h00 - 00h00</p>
            </div>
          </div>
        </div>

        {/* Instagram */}
        <div className="mt-6 flex justify-center">
          <a 
            href="https://www.instagram.com/popboxnice" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-pink-600 via-purple-600 to-orange-500 hover:from-pink-500 hover:via-purple-500 hover:to-orange-400 text-white font-bold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-pink-500/20"
            data-testid="instagram-hours-link"
          >
            <Instagram className="w-5 h-5" />
            @popboxnice
          </a>
        </div>
      </div>
    </section>
  );
};

// Birthday Section
const BirthdaySection = () => {
  return (
    <section id="anniversaire" data-testid="birthday-section" className="py-20 px-4 bg-gradient-to-br from-gray-900 via-pink-900/15 to-gray-900 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-pink-500/10 rounded-full blur-2xl" />
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <Cake className="w-8 h-8 text-pink-400" />
            <h2 className="text-4xl md:text-5xl font-bold orbitron-font">
              <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Anniversaire Enfants
              </span>
            </h2>
            <PartyPopper className="w-8 h-8 text-purple-400" />
          </div>
          <p className="text-lg text-gray-300 noto-sans-font max-w-2xl mx-auto">
            Offrez une fête inoubliable dans nos boxes karaoké privés !
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Tarif */}
          <Card className="bg-gradient-to-br from-pink-900/40 to-purple-900/30 border-pink-500/40 hover:border-pink-500/60 transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-xl orbitron-font flex items-center gap-2">
                <Music className="w-5 h-5 text-pink-400" />
                Tarif
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-pink-400 mb-1 orbitron-font">10€</div>
              <p className="text-gray-300 noto-sans-font">par enfant / heure</p>
              <p className="text-sm text-cyan-400 mt-3 noto-sans-font font-medium">Bonbons et boissons inclus !</p>
            </CardContent>
          </Card>

          {/* Capacité */}
          <Card className="bg-gradient-to-br from-purple-900/40 to-cyan-900/30 border-purple-500/40 hover:border-purple-500/60 transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-xl orbitron-font flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Capacité
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-purple-400 mb-1 orbitron-font">12</div>
              <p className="text-gray-300 noto-sans-font">enfants maximum</p>
            </CardContent>
          </Card>
        </div>

        {/* Infos importantes */}
        <div className="mt-8 space-y-4">
          <Card className="bg-gray-800/60 border-cyan-500/30">
            <CardContent className="pt-6">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="mt-0.5 p-2 rounded-lg bg-pink-500/15">
                    <ShieldCheck className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  </div>
                  <div>
                    <p className="text-white font-semibold noto-sans-font">Accompagnateur adulte obligatoire</p>
                    <p className="text-gray-400 text-sm noto-sans-font mt-1">
                      L'accompagnateur doit rester dans l'établissement pendant toute la durée de la session, même s'il n'est pas dans le box où se déroule l'événement.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-0.5 p-2 rounded-lg bg-purple-500/15">
                    <Users className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  </div>
                  <div>
                    <p className="text-white font-semibold noto-sans-font">À partir de 12 ans</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-0.5 p-2 rounded-lg bg-cyan-500/15">
                    <CalendarDays className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  </div>
                  <div>
                    <p className="text-white font-semibold noto-sans-font">Lors de la réservation</p>
                    <p className="text-gray-400 text-sm noto-sans-font mt-1">
                      Précisez l'âge de l'enfant ainsi que le nombre exact de participants (à +/- 2). Nous réactualiserons le nombre exact à votre arrivée.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-0.5 p-2 rounded-lg bg-pink-500/15">
                    <Cake className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  </div>
                  <div>
                    <p className="text-white font-semibold noto-sans-font">Gâteau d'anniversaire autorisé</p>
                    <p className="text-gray-400 text-sm noto-sans-font mt-1">
                      Pensez à prévoir assiettes et couverts !
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Booking Form Component
const BookingForm = ({ onSuccess, preselectedRoom }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    room_id: preselectedRoom || '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    booking_date: '',
    start_time: '',
    duration_hours: 1,
    number_of_people: 1,
    special_requests: '',
    payment_mode: 'full',  // 'full' or 'split'
    number_of_payers: 1,
    custom_amount: ''  // For split payment custom amount
  });
  
  const [availableSlots, setAvailableSlots] = useState([]);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Calculate available durations based on start time (closing at midnight)
  const getAvailableDurations = (startTime) => {
    if (!startTime) return [1, 2, 3, 4, 5];
    
    try {
      const [hours, minutes] = startTime.split(':').map(Number);
      const startMinutes = hours * 60 + minutes;
      const midnightMinutes = 24 * 60; // Minuit = 0h du lendemain
      const availableMinutes = midnightMinutes - startMinutes;
      const maxHours = Math.floor(availableMinutes / 60);
      
      // Générer la liste des durées disponibles (de 1h jusqu'à maxHours)
      const durations = [];
      for (let i = 1; i <= Math.min(maxHours, 5); i++) {
        durations.push(i);
      }
      return durations.length > 0 ? durations : [1];
    } catch (e) {
      return [1, 2, 3, 4, 5];
    }
  };

  // Reset duration if it becomes invalid when start time changes
  useEffect(() => {
    if (formData.start_time) {
      const availableDurations = getAvailableDurations(formData.start_time);
      if (!availableDurations.includes(formData.duration_hours)) {
        setFormData(prev => ({ ...prev, duration_hours: availableDurations[0] || 1 }));
      }
    }
  }, [formData.start_time, formData.duration_hours]);

  // Update room_id when preselectedRoom changes
  useEffect(() => {
    if (preselectedRoom && preselectedRoom !== formData.room_id) {
      setFormData(prev => ({ ...prev, room_id: preselectedRoom }));
    }
  }, [preselectedRoom]);

  const calculatePrice = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/bookings/calculate-price`, {
        room_id: formData.room_id,
        start_time: formData.start_time,
        duration_hours: formData.duration_hours,
        number_of_people: formData.number_of_people
      });
      setCalculatedPrice(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  }, [formData.room_id, formData.start_time, formData.duration_hours, formData.number_of_people]);

  const fetchAvailableSlots = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/rooms/${formData.room_id}/availability?date_str=${formData.booking_date}`
      );
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);
    }
  }, [formData.room_id, formData.booking_date]);

  // Calculate price when form data changes
  useEffect(() => {
    if (formData.room_id && formData.start_time && formData.duration_hours && formData.number_of_people) {
      calculatePrice();
    }
  }, [calculatePrice, formData.room_id, formData.start_time, formData.duration_hours, formData.number_of_people]);

  // Get available slots when room and date change
  useEffect(() => {
    if (formData.room_id && formData.booking_date) {
      fetchAvailableSlots();
    }
  }, [fetchAvailableSlots, formData.room_id, formData.booking_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des champs requis
    if (!formData.room_id || !formData.customer_name || !formData.customer_email || 
        !formData.customer_phone || !formData.booking_date || !formData.start_time) {
      toast({
        title: "Champs manquants",
        description: "Veuillez remplir tous les champs obligatoires (*)",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);

    try {
      // Create booking
      const response = await axios.post(`${API}/bookings`, formData);
      const bookingData = response.data;

      // Determine payment amount
      let paymentAmount = null;
      if (formData.payment_mode === 'split' && formData.custom_amount) {
        paymentAmount = parseFloat(formData.custom_amount);
      } else if (formData.payment_mode === 'split' && calculatedPrice?.price_per_person) {
        paymentAmount = calculatedPrice.price_per_person;
      }

      // Create payment session
      const paymentResponse = await axios.post(`${API}/payments/checkout`, {
        booking_id: bookingData.booking_id,
        origin_url: window.location.origin,
        custom_amount: paymentAmount
      });

      // Validate checkout URL before redirect
      if (!paymentResponse.data?.checkout_url) {
        throw new Error("URL de paiement invalide");
      }

      console.log("Redirection vers Stripe:", paymentResponse.data.checkout_url);

      // Redirect to Stripe checkout
      window.location.href = paymentResponse.data.checkout_url;

    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur s'est produite lors de la réservation",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      // CORRECTION CRITIQUE: Ne pas utiliser toISOString() qui convertit en UTC
      // Utiliser getFullYear/getMonth/getDate pour garder la date locale
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      setFormData(prev => ({ ...prev, booking_date: dateStr }));
      setCalendarOpen(false); // Fermeture automatique du calendrier
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card className="bg-gray-800/80 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-white text-2xl flex items-center orbitron-font">
            <CalendarDays className="w-6 h-6 text-pink-400 mr-2" />
            {t('booking_title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Room Selection */}
            <div>
              <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_room')} *</label>
              <Select 
                value={formData.room_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, room_id: value }))}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white noto-sans-font">
                  <SelectValue placeholder={t('booking_room_select')} />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {Object.entries(ROOMS)
                    .sort(([, a], [, b]) => a.capacity - b.capacity) // Tri par capacité croissante
                    .map(([roomId, room]) => (
                    <SelectItem key={roomId} value={roomId} className="text-white noto-sans-font">
                      {room.name} (max {room.capacity} pers.)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_name')} *</label>
                <Input
                  className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_phone')} *</label>
                <Input
                  type="tel"
                  className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_email')} *</label>
              <Input
                type="email"
                className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                value={formData.customer_email}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                required
              />
            </div>

            {/* Date Selection */}
            <div>
              <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_date')} *</label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full bg-gray-700 border-gray-600 text-white justify-start text-left noto-sans-font"
                  >
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {selectedDate ? selectedDate.toLocaleDateString('fr-FR') : t('booking_date_select')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-600">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => date < new Date(2026, 3, 11)}
                    fromDate={new Date(2026, 3, 11)}
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Slot Selection */}
            {/* Time Selection */}
            <div>
              <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_time')} *</label>
              {formData.room_id && formData.booking_date ? (
                availableSlots.length > 0 ? (
                  <Select 
                    value={formData.start_time}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, start_time: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white noto-sans-font">
                      <SelectValue placeholder={t('booking_time_select')} />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {availableSlots.map((slot) => (
                        <SelectItem key={slot} value={slot} className="text-white noto-sans-font">
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="bg-gray-700 border border-gray-600 rounded p-3 text-gray-400 text-sm">
                    Aucun créneau disponible pour cette date
                  </div>
                )
              ) : (
                <div className="bg-gray-700 border border-gray-600 rounded p-3 text-gray-400 text-sm">
                  Veuillez d'abord sélectionner un box et une date
                </div>
              )}
            </div>

            {/* Duration and People */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_duration')} *</label>
                <Select 
                  value={formData.duration_hours.toString()}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, duration_hours: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white noto-sans-font">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {getAvailableDurations(formData.start_time).map((hours) => (
                      <SelectItem key={hours} value={hours.toString()} className="text-white noto-sans-font">
                        {hours} {hours === 1 ? t('booking_hour') : t('booking_hours')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_people')} *</label>
                <Select 
                  value={formData.number_of_people.toString()}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, number_of_people: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white noto-sans-font">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {formData.room_id && ROOMS[formData.room_id] && 
                      Array.from({ length: ROOMS[formData.room_id].capacity }, (_, i) => i + 1).map((num) => (
                        <SelectItem key={num} value={num.toString()} className="text-white noto-sans-font">
                          {num} {num === 1 ? t('room_person') : t('room_people')}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>


            {/* Payment Mode */}
            <div className="bg-gray-800/50 p-4 rounded-lg border border-purple-500/30">
              <label className="block text-white mb-3 font-semibold orbitron-font">
                💳 Mode de Paiement
              </label>
              <div className="space-y-3">
                <div 
                  onClick={() => setFormData(prev => ({ ...prev, payment_mode: 'full', number_of_payers: 1 }))}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.payment_mode === 'full' 
                      ? 'border-pink-500 bg-pink-500/10' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      formData.payment_mode === 'full' ? 'border-pink-500' : 'border-gray-500'
                    }`}>
                      {formData.payment_mode === 'full' && (
                        <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                      )}
                    </div>
                    <div>
                      <div className="text-white font-semibold">Paiement Complet</div>
                      <div className="text-gray-400 text-sm">Une personne paie pour tout le monde (100%)</div>
                    </div>
                  </div>
                </div>
                
                <div 
                  onClick={() => setFormData(prev => ({ ...prev, payment_mode: 'split', number_of_payers: prev.number_of_people }))}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.payment_mode === 'split' 
                      ? 'border-cyan-500 bg-cyan-500/10' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      formData.payment_mode === 'split' ? 'border-cyan-500' : 'border-gray-500'
                    }`}>
                      {formData.payment_mode === 'split' && (
                        <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-semibold">Payer à Plusieurs</div>
                      <div className="text-gray-400 text-sm">Chaque personne paie sa part ({calculatedPrice?.price_per_person?.toFixed(2)}€/pers - min. 50% requis)</div>
                    </div>
                  </div>
                </div>
                
                {formData.payment_mode === 'split' && (
                  <div className="mt-3 p-3 bg-cyan-900/20 border border-cyan-500/30 rounded">
                    <div className="flex items-start gap-2">
                      <span className="text-xl">💡</span>
                      <div className="text-sm text-cyan-300">
                        Un lien partageable sera généré après votre paiement pour que les {formData.number_of_people - 1} autres personnes puissent payer leur part.
                      </div>
                    </div>
                    {/* Timer info based on peak hours */}
                    {formData.start_time && (
                      <div className="mt-2 pt-2 border-t border-cyan-500/20">
                        <p className="text-xs text-yellow-300">
                          ⏳ Vous avez {parseInt(formData.start_time.split(':')[0]) >= 19 ? '20' : '30'} minutes pour arriver à 50% du montant, on y est presque 😅 Go Go Goooo
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>


            {/* Special Requests */}
            <div>
              <label className="block text-white mb-2 font-semibold orbitron-font">{t('booking_requests')}</label>
              <Textarea
                className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                placeholder={t('booking_requests_placeholder')}
                value={formData.special_requests}
                onChange={(e) => setFormData(prev => ({ ...prev, special_requests: e.target.value }))}
              />
            </div>

            {/* Price Display */}
            {calculatedPrice && (
              <div className="p-6 bg-gradient-to-r from-pink-900/30 to-purple-900/30 rounded-lg border border-pink-500/30">
                <h3 className="text-lg font-semibold text-white mb-3 orbitron-font">{t('booking_price_title')}</h3>
                
                {/* Special Rate Badge */}
                {calculatedPrice.is_special_rate && (
                  <div className="mb-3 p-3 bg-green-900/30 border border-green-500/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🎉</span>
                      <div>
                        <p className="text-green-400 font-bold text-sm">Tarif Dégressif Appliqué!</p>
                        <p className="text-green-300 text-xs">3h+ = 8€/personne/heure peu importe l'horaire</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-3">
                  {/* Total Price */}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 noto-sans-font">Prix total:</span>
                    <span className="text-3xl font-bold text-pink-400 orbitron-font">
                      {calculatedPrice.total_price}€
                    </span>
                  </div>
                  
                  {/* Price per Person */}
                  {calculatedPrice.price_per_person && (
                    <div className="flex justify-between items-center border-t border-gray-700 pt-3">
                      <span className="text-gray-300 noto-sans-font">Prix par personne:</span>
                      <span className="text-2xl font-bold text-cyan-400 orbitron-font">
                        {calculatedPrice.price_per_person}€
                      </span>
                    </div>
                  )}
                  
                  {/* Minimum Deposit - Only show for split payment */}
                  {calculatedPrice.min_deposit_required && formData.payment_mode === 'split' && (
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 mt-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-yellow-300 text-sm font-semibold">Acompte minimum (50%):</span>
                        <span className="text-xl font-bold text-yellow-400 orbitron-font">
                          {calculatedPrice.min_deposit_required}€
                        </span>
                      </div>
                      <p className="text-yellow-200 text-xs text-center">
                        💡 50% du montant requis pour que la réservation soit définitive 😊
                      </p>
                    </div>
                  )}
                  
                  {/* Multiple Hours Bonus */}
                  {formData.duration_hours > 1 && (
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded p-3 mt-3">
                      <div className="flex items-start gap-2">
                        <span className="text-xl">⏰</span>
                        <div>
                          <p className="text-purple-300 font-semibold text-sm">Bonus réservation multiple!</p>
                          <p className="text-purple-200 text-xs mt-1">
                            Vous gagnez les 10 min de battement entre les sessions (pas de pause ménage)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Encouraging Message */}
                  {formData.number_of_people > 1 && (
                    <div className="text-center mt-3 p-2 bg-cyan-900/20 rounded">
                      <p className="text-cyan-300 text-sm font-semibold noto-sans-font">
                        💸 Plus vous êtes, moins vous paierez individuellement !
                      </p>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-400 noto-sans-font mt-2">
                    {calculatedPrice.breakdown.duration_hours} {calculatedPrice.breakdown.duration_hours === 1 ? t('booking_hour') : t('booking_hours')} × {calculatedPrice.breakdown.number_of_people} {calculatedPrice.breakdown.number_of_people === 1 ? t('room_person') : t('room_people')}
                    {calculatedPrice.is_special_rate && ' (tarif dégressif)'}
                  </p>
                </div>
              </div>
            )}

            {/* Custom Amount for Split Payment */}
            {formData.payment_mode === 'split' && calculatedPrice && (
              <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
                <label className="block text-cyan-300 mb-2 font-semibold text-sm orbitron-font">
                  💰 Montant à payer (optionnel)
                </label>
                <div className="space-y-2">
                  <Input
                    type="number"
                    min={0}
                    max={calculatedPrice.total_price}
                    step="0.01"
                    placeholder={`Suggestion: ${calculatedPrice.price_per_person}€ (votre part)`}
                    className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                    value={formData.custom_amount || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_amount: e.target.value }))}
                  />
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, custom_amount: calculatedPrice.price_per_person }))}
                      className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 text-white text-xs rounded"
                    >
                      Ma part ({calculatedPrice.price_per_person}€)
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, custom_amount: calculatedPrice.min_deposit_required }))}
                      className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded"
                    >
                      50% minimum ({calculatedPrice.min_deposit_required}€)
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, custom_amount: calculatedPrice.total_price }))}
                      className="px-3 py-1 bg-pink-600 hover:bg-pink-700 text-white text-xs rounded"
                    >
                      Total ({calculatedPrice.total_price}€)
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    Laissez vide pour payer votre part automatiquement ({calculatedPrice.price_per_person}€)
                  </p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !calculatedPrice}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-3 font-bold text-lg orbitron-font"
            >
              {loading ? t('booking_processing') : `${t('booking_submit')} ${calculatedPrice ? (
                formData.payment_mode === 'split' 
                  ? (formData.custom_amount || calculatedPrice.price_per_person) + '€'
                  : calculatedPrice.total_price + '€'
              ) : ''}`}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Contact Section with ADDRESS
const ContactSection = () => {
  const { t } = useLanguage();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      toast({
        title: t('toast_message_sent'),
        description: t('toast_message_sent_desc')
      });
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast({
        title: t('toast_error'),
        description: t('toast_message_error'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 bg-gradient-to-br from-gray-900 to-cyan-900/20">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 orbitron-font">
            <span className="bg-gradient-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
              {t('contact_title')}
            </span>
          </h2>
          <p className="text-xl text-gray-300 noto-sans-font">
            {t('contact_subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info avec ADRESSE COMPLETE */}
          <div className="space-y-8">
            <Card className="bg-gray-800/50 border-cyan-500/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-white mb-6 orbitron-font">POP BOX KARAOKE NICE</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-pink-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium orbitron-font">{t('contact_address')}</p>
                      <p className="text-gray-300 noto-sans-font">41, rue de l'Hôtel des Postes</p>
                      <p className="text-gray-300 noto-sans-font">06000 Nice, France</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Train className="w-5 h-5 text-cyan-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium orbitron-font">{t('contact_transport')}</p>
                      <p className="text-gray-300 noto-sans-font">{t('contact_transport_stop')}</p>
                      <p className="text-sm text-gray-400 noto-sans-font">{t('contact_transport_info')}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-cyan-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium orbitron-font">{t('contact_phone')}</p>
                      <p className="text-gray-300 noto-sans-font">{t('contact_phone_form')}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium orbitron-font">{t('contact_email')}</p>
                      <p className="text-gray-300 noto-sans-font">contact@popboxkaraoke.com</p>
                      <p className="text-sm text-gray-400 noto-sans-font">{t('contact_email_guarantee')}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-pink-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium orbitron-font">{t('contact_hours')}</p>
                      <p className="text-gray-300 text-sm noto-sans-font">
                        {t('contact_hours_week')}<br />
                        {t('contact_hours_weekend')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          {/* Contact Form */}
          <Card className="bg-gray-800/50 border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-white text-xl orbitron-font">{t('contact_form_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <Input
                    placeholder={t('contact_name_placeholder')}
                    className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                
                <div>
                  <Input
                    type="email"
                    placeholder={t('contact_email_placeholder')}
                    className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
                
                <div>
                  <Input
                    type="tel"
                    placeholder={t('contact_phone_placeholder')}
                    className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Textarea
                    placeholder={t('contact_message_placeholder')}
                    className="bg-gray-700 border-gray-600 text-white min-h-[120px] noto-sans-font"
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 orbitron-font font-bold"
                >
                  {loading ? t('contact_sending') : t('contact_send')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    </section>
  );
};

// Payment Status Component
const PaymentStatus = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [bookingData, setBookingData] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [location]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        
        // Fetch booking details to get share token
        if (data.booking_id) {
          try {
            const bookingResponse = await axios.get(`${API}/bookings/${data.booking_id}`);
            setBookingData(bookingResponse.data);
          } catch (err) {
            console.error('Error fetching booking:', err);
          }
        }
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  const getStatusContent = () => {
    switch (status) {
      case 'checking':
        return {
          title: t('payment_checking'),
          message: t('payment_checking_msg'),
          color: 'text-yellow-400',
          action: null
        };
      case 'success':
        return {
          title: t('payment_success'),
          message: t('payment_success_msg'),
          color: 'text-green-400',
          action: (
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 orbitron-font font-bold"
            >
              {t('payment_back_home')}
            </Button>
          )
        };
      case 'expired':
        return {
          title: t('payment_expired'),
          message: t('payment_expired_msg'),
          color: 'text-orange-400',
          action: (
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 orbitron-font font-bold"
            >
              {t('payment_new_booking')}
            </Button>
          )
        };
      default:
        return {
          title: t('payment_error'),
          message: t('payment_error_msg'),
          color: 'text-red-400',
          action: (
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-red-400 text-red-400 hover:bg-red-400 hover:text-white orbitron-font font-bold"
            >
              {t('payment_back_home')}
            </Button>
          )
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <Card className="max-w-2xl w-full bg-gray-800 border-purple-500/30 text-center">
        <CardContent className="p-8">
          <div className={`text-6xl mb-6 ${statusContent.color}`}>
            {status === 'checking' && '⏳'}
            {status === 'success' && '✅'}
            {status === 'expired' && '⏰'}
            {status === 'error' && '❌'}
          </div>
          
          <h1 className={`text-2xl font-bold mb-4 ${statusContent.color} orbitron-font`}>
            {statusContent.title}
          </h1>
          
          <p className="text-gray-300 mb-6 noto-sans-font">
            {statusContent.message}
          </p>

          {paymentData && (
            <div className="bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-white mb-2 orbitron-font">{t('payment_details')}</h3>
              <p className="text-sm text-gray-300 noto-sans-font">
                {t('payment_amount')}: {(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency.toUpperCase()}
              </p>
            </div>
          )}
          
          {/* Share link for split payment */}
          {status === 'success' && bookingData && bookingData.payment_mode === 'split' && bookingData.share_token && (
            <div className="bg-cyan-900/30 border border-cyan-500/50 rounded-lg p-6 mb-6">
              <h3 className="text-cyan-400 font-bold text-lg mb-3 orbitron-font">
                🔗 Paiement à Plusieurs
              </h3>
              
              <div className="bg-gray-900/50 p-4 rounded mb-4">
                <p className="text-white mb-2 text-sm">
                  {bookingData.payments?.length || 1} / {bookingData.number_of_payers} personnes ont payé
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-cyan-400 font-bold text-2xl">{bookingData.amount_paid.toFixed(2)}€</p>
                    <p className="text-gray-400 text-sm">sur {bookingData.total_price.toFixed(2)}€</p>
                  </div>
                  <div className="text-right">
                    <p className="text-orange-400 font-bold text-xl">{(bookingData.total_price - bookingData.amount_paid).toFixed(2)}€</p>
                    <p className="text-gray-400 text-sm">reste à payer</p>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-300 text-sm mb-3">
                Partagez ce lien avec vos amis pour qu'ils puissent payer leur part:
              </p>
              
              <div className="bg-gray-900 p-3 rounded mb-3 break-all text-cyan-300 text-sm font-mono">
                {window.location.origin}/booking/shared/{bookingData.share_token}
              </div>
              
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/booking/shared/${bookingData.share_token}`);
                  toast({ title: "Lien copié!", description: "Le lien a été copié dans le presse-papier" });
                }}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold orbitron-font"
              >
                📋 Copier le Lien
              </Button>
              
              {bookingData.amount_paid < bookingData.min_amount_required && (
                <p className="text-yellow-400 text-xs mt-3">
                  ⚠️ Minimum 50% requis pour confirmer la réservation
                </p>
              )}
            </div>
          )}
          
          {statusContent.action}
        </CardContent>
      </Card>
    </div>
  );
};





// Chronological Calendar View Component (by dates)
const ChronologicalCalendarView = ({ authToken, t, bookings, blockedSlots = [] }) => {
  const [expandedDates, setExpandedDates] = useState({});
  const [showPastDates, setShowPastDates] = useState(false);

  // Convert walk-in blocked slots to booking-like objects for display
  const walkInSlots = blockedSlots
    .filter(s => s.booking_type === 'walk_in')
    .map(s => ({
      ...s,
      booking_date: s.blocked_date,
      customer_name: s.customer_name || 'Client boutique',
      number_of_people: '-',
      amount_paid: 0,
      total_price: 0,
      duration_hours: s.duration_hours || 1,
      payment_method: s.payment_method || '',
      is_walk_in: true
    }));

  // Merge bookings + walk-in slots
  const allEntries = [...bookings, ...walkInSlots];

  // Group by date
  const bookingsByDate = allEntries.reduce((acc, booking) => {
    const date = booking.booking_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(booking);
    return acc;
  }, {});

  // Sort dates chronologically
  const sortedDates = Object.keys(bookingsByDate).sort((a, b) => {
    // Direct string comparison works for YYYY-MM-DD format
    return a.localeCompare(b);
  });

  // Sort bookings within each date by time
  sortedDates.forEach(date => {
    bookingsByDate[date].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  });

  const todayStr = new Date().toISOString().split('T')[0];
  const futureDates = sortedDates.filter(d => d >= todayStr);
  const pastDatesCount = sortedDates.filter(d => d < todayStr).length;
  const visibleDates = showPastDates ? sortedDates : futureDates;

  const toggleDate = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Auto-expand today and upcoming 3 days
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const dates = Object.keys(bookingsByDate).sort((a, b) => a.localeCompare(b));
    const upcomingDates = dates.filter(d => d >= today).slice(0, 3);
    const initialExpanded = {};
    upcomingDates.forEach(d => {
      initialExpanded[d] = true;
    });
    setExpandedDates(initialExpanded);
  }, [bookings]);

  const formatDateHeader = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    
    const dayName = formatDateStringLong(dateStr);
    
    if (dateStr === today) {
      return `🔴 Aujourd'hui - ${dayName}`;
    } else if (dateStr === tomorrow) {
      return `🟡 Demain - ${dayName}`;
    } else {
      return dayName.charAt(0).toUpperCase() + dayName.slice(1);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl text-white orbitron-font">Calendrier des Réservations</h2>
        <div className="flex items-center gap-3">
          {pastDatesCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPastDates(!showPastDates)}
              className="border-gray-500 text-gray-400 hover:text-white text-xs"
            >
              {showPastDates ? 'Masquer passées' : `Voir passées (${pastDatesCount})`}
            </Button>
          )}
          <div className="text-gray-400 text-sm">
            {visibleDates.length} dates • {visibleDates.reduce((sum, d) => sum + (bookingsByDate[d]?.length || 0), 0)} réservations
          </div>
        </div>
      </div>

      {visibleDates.length === 0 ? (
        <Card className="bg-gray-800 border-gray-600">
          <CardContent className="p-8 text-center">
            <p className="text-gray-400">Aucune réservation à venir</p>
          </CardContent>
        </Card>
      ) : (
        visibleDates.map(date => {
          const isExpanded = expandedDates[date];
          const dateBookings = bookingsByDate[date];
          const isToday = date === new Date().toISOString().split('T')[0];
          
          return (
            <Card key={date} className={`${isToday ? 'border-cyan-500 border-2' : 'border-gray-600'} bg-gray-700/50`}>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-700 transition-colors p-4"
                onClick={() => toggleDate(date)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {isExpanded ? '➖' : '➕'}
                    </span>
                    <div>
                      <h3 className="text-white font-bold text-lg orbitron-font">
                        {formatDateHeader(date)}
                      </h3>
                      <p className="text-gray-400 text-sm">
                        {dateBookings.length} réservation{dateBookings.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-gray-400 text-sm">
                    {dateBookings.reduce((sum, b) => sum + b.total_price, 0)}€ total
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="p-4 pt-0 space-y-2">
                  {dateBookings.map((booking, idx) => {
                    // Walk-in / boutique reservation
                    if (booking.is_walk_in) {
                      const displayTime = (booking.start_time || '').slice(0, 5);
                      return (
                        <Card key={`walkin-${idx}`} className="border-l-4 border-l-cyan-500 bg-cyan-900/10">
                          <CardContent className="p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center gap-4">
                                <span className="text-2xl font-bold text-cyan-400 orbitron-font min-w-[70px]">
                                  {displayTime}
                                </span>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-white font-bold orbitron-font">
                                      {booking.room_id?.toUpperCase()}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-white">{booking.customer_name}</span>
                                    {booking.customer_phone && <span className="text-gray-400 text-sm">• {booking.customer_phone}</span>}
                                  </div>
                                  <div className="text-sm text-gray-400">
                                    {booking.duration_hours}h {booking.reason && booking.reason !== 'Réservation boutique' ? `• ${booking.reason}` : ''}
                                  </div>
                                </div>
                              </div>
                              <Badge className="bg-cyan-500/20 text-cyan-300">Boutique</Badge>
                                    {booking.payment_method === 'cash' && <Badge className="bg-green-500/20 text-green-300">Payé</Badge>}
                                    {booking.payment_method === 'offered' && <Badge className="bg-green-500/20 text-green-300">Offert</Badge>}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    // Regular booking
                    const percentPaid = (booking.amount_paid / booking.total_price) * 100;
                    const remaining = booking.total_price - booking.amount_paid;
                    const statusColor = percentPaid >= 100 ? 'green' : percentPaid >= 50 ? 'yellow' : 'red';

                    return (
                      <Card 
                        key={idx} 
                        className={`border-l-4 cursor-pointer hover:bg-gray-600 transition-colors ${
                          statusColor === 'red' ? 'border-l-red-500 bg-red-900/10' :
                          statusColor === 'green' ? 'border-l-green-500 bg-green-900/10' :
                          'border-l-yellow-500 bg-yellow-900/10'
                        }`}
                        onClick={() => {
                          // This will be handled by parent component via selectedBooking
                          const event = new CustomEvent('openBookingDetail', { detail: booking });
                          window.dispatchEvent(event);
                        }}
                      >
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-4">
                              <span className="text-2xl font-bold text-cyan-400 orbitron-font min-w-[70px]">
                                {booking.start_time}
                              </span>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-white font-bold orbitron-font">
                                    {booking.room_id?.toUpperCase()}
                                  </span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-white">{booking.customer_name}</span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-purple-300">👥 {booking.number_of_people}</span>
                                </div>
                                <div className="text-sm text-gray-400">
                                  ⏱️ {booking.duration_hours}h • 💰 {booking.amount_paid}€ / {booking.total_price}€
                                  {remaining > 0 && (
                                    <span className="text-orange-400 ml-2">
                                      (Reste: {remaining.toFixed(2)}€)
                                    </span>
                                  )}
                                </div>
                                {booking.special_requests && (
                                  <p className="text-purple-300 text-xs mt-1">
                                    💬 {booking.special_requests.substring(0, 50)}{booking.special_requests.length > 50 ? '...' : ''}
                                  </p>
                                )}
                                {booking.internal_notes && booking.internal_notes.length > 0 && (
                                  <p className="text-cyan-300 text-xs mt-1">
                                    📝 {booking.internal_notes.length} note{booking.internal_notes.length > 1 ? 's' : ''} interne{booking.internal_notes.length > 1 ? 's' : ''}
                                  </p>
                                )}
                                {/* Stats fidélité client */}
                                <div className="mt-2 text-xs">
                                  {booking.customer_total_bookings <= 1 ? (
                                    <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full">1ère réservation</span>
                                  ) : (
                                    <span className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full">
                                      {booking.customer_total_bookings} réservations • {booking.customer_total_paid}€ total
                                      {booking.customer_last_booking && ` • Dernière le ${new Date(booking.customer_last_booking).toLocaleDateString('fr-FR')}`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className={`px-3 py-1 rounded text-sm font-bold ${
                              statusColor === 'red' ? 'bg-red-900/50 text-red-300' :
                              statusColor === 'green' ? 'bg-green-900/50 text-green-300' :
                              'bg-yellow-900/50 text-yellow-300'
                            }`}>
                              {Math.round(percentPaid)}%
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
};


// Daily Planning View Component (for employees)
const DailyPlanningView = ({ authToken, t, blockedSlots = [] }) => {
  const [todayBookings, setTodayBookings] = useState([]);
  const [tomorrowBookings, setTomorrowBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const loadDailyData = useCallback(async () => {
    try {
      // Load daily stats
      const statsResponse = await axios.get(`${API}/admin/stats/daily`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      setStats(statsResponse.data);

      // Load all bookings
      const bookingsResponse = await axios.get(`${API}/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      const today = new Date().toISOString().split('T')[0];
      const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

      // Filter today's bookings
      const todayList = bookingsResponse.data.filter(b => b.booking_date === today);

      // Add walk-in slots for today
      const todayWalkIns = blockedSlots
        .filter(s => s.booking_type === 'walk_in' && s.blocked_date === today)
        .map(s => ({ ...s, booking_date: s.blocked_date, customer_name: s.customer_name || 'Client boutique', duration_hours: s.duration_hours || 1, payment_method: s.payment_method || '', is_walk_in: true }));
      todayList.push(...todayWalkIns);

      // Sort by start_time
      todayList.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
      setTodayBookings(todayList);

      // Filter tomorrow's bookings
      const tomorrowList = bookingsResponse.data.filter(b => b.booking_date === tomorrow);

      // Add walk-in slots for tomorrow
      const tomorrowWalkIns = blockedSlots
        .filter(s => s.booking_type === 'walk_in' && s.blocked_date === tomorrow)
        .map(s => ({ ...s, booking_date: s.blocked_date, customer_name: s.customer_name || 'Client boutique', duration_hours: s.duration_hours || 1, payment_method: s.payment_method || '', is_walk_in: true }));
      tomorrowList.push(...tomorrowWalkIns);

      tomorrowList.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
      setTomorrowBookings(tomorrowList);

    } catch (error) {
      console.error('Error loading daily data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authToken, blockedSlots]);

  useEffect(() => {
    loadDailyData();
  }, [loadDailyData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-white text-lg">⏳ Chargement du planning...</p>
      </div>
    );
  }

  const renderBookingCard = (booking) => {
    // Walk-in / boutique reservation
    if (booking.is_walk_in) {
      const displayTime = (booking.start_time || '').slice(0, 5);
      return (
        <Card key={`walkin-${booking.id}`} className="border-l-4 border-l-cyan-500 bg-cyan-900/10 bg-gray-700/50">
          <CardContent className="p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl font-bold text-cyan-400 orbitron-font">{displayTime}</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-lg text-white orbitron-font">{booking.room_id?.toUpperCase()}</span>
                </div>
                <p className="text-gray-300 text-sm">{booking.duration_hours}h</p>
              </div>
              <Badge className="bg-cyan-500/20 text-cyan-300 px-3 py-1">Boutique</Badge>
              {booking.payment_method === 'cash' && <Badge className="bg-green-500/20 text-green-300 px-2 py-1">Payé</Badge>}
              {booking.payment_method === 'offered' && <Badge className="bg-green-500/20 text-green-300 px-2 py-1">Offert</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <p className="text-gray-400 text-xs mb-1">Client</p>
                <p className="text-white font-semibold">{booking.customer_name}</p>
                {booking.customer_phone && <p className="text-cyan-400 text-sm">{booking.customer_phone}</p>}
              </div>
              {booking.customer_email && (
                <div>
                  <p className="text-gray-400 text-xs mb-1">Email</p>
                  <p className="text-cyan-400 text-sm">{booking.customer_email}</p>
                </div>
              )}
            </div>
            {booking.reason && booking.reason !== 'Réservation boutique' && (
              <p className="text-purple-300 text-xs mt-1">{booking.reason}</p>
            )}
          </CardContent>
        </Card>
      );
    }

    const percentPaid = (booking.amount_paid / booking.total_price) * 100;
    const remaining = booking.total_price - booking.amount_paid;
    const statusColor = percentPaid >= 100 ? 'green' : percentPaid >= 50 ? 'yellow' : 'red';

    return (
      <Card key={booking.id} className={`border-l-4 ${
        statusColor === 'red' ? 'border-l-red-500 bg-red-900/10' :
        statusColor === 'green' ? 'border-l-green-500 bg-green-900/10' :
        'border-l-yellow-500 bg-yellow-900/10'
      } bg-gray-700/50`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl font-bold text-cyan-400 orbitron-font">
                  {booking.start_time}
                </span>
                <span className="text-gray-400">→</span>
                <span className="text-lg text-white orbitron-font">
                  {booking.room_id?.toUpperCase()}
                </span>
              </div>
              <p className="text-gray-300 text-sm">
                ⏱️ Durée: {booking.duration_hours}h
              </p>
            </div>
            <div className={`px-4 py-2 rounded-lg text-sm font-bold ${
              statusColor === 'red' ? 'bg-red-900/50 text-red-300' :
              statusColor === 'green' ? 'bg-green-900/50 text-green-300' :
              'bg-yellow-900/50 text-yellow-300'
            }`}>
              {Math.round(percentPaid)}%
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <p className="text-gray-400 text-xs mb-1">Client</p>
              <p className="text-white font-semibold">{booking.customer_name}</p>
              <p className="text-cyan-400 text-sm">{booking.customer_email}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-1">Participants</p>
              <p className="text-white text-2xl font-bold orbitron-font">
                👥 {booking.number_of_people}
              </p>
            </div>
          </div>

          <div className="bg-gray-800/50 p-3 rounded mb-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-gray-400 text-xs">Total</p>
                <p className="text-white font-bold">{booking.total_price}€</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Encaissé</p>
                <p className="text-green-400 font-bold">{booking.amount_paid}€</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Reste</p>
                <p className="text-orange-400 font-bold">{remaining.toFixed(2)}€</p>
              </div>
            </div>
          </div>

          {booking.special_requests && (
            <div className="bg-purple-900/20 p-2 rounded border border-purple-500/30">
              <p className="text-purple-300 text-xs">
                💬 Commentaires: {booking.special_requests}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards - Just today and week */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-cyan-900/50 to-cyan-800/30 border-cyan-500/30">
            <CardContent className="p-4">
              <p className="text-cyan-300 text-sm font-semibold mb-1">CA Aujourd'hui</p>
              <p className="text-white text-3xl font-bold orbitron-font">{stats.today_revenue}€</p>
              <p className="text-cyan-400 text-xs">{stats.today_bookings} réservations</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-500/30">
            <CardContent className="p-4">
              <p className="text-purple-300 text-sm font-semibold mb-1">CA Semaine</p>
              <p className="text-white text-3xl font-bold orbitron-font">{stats.week_revenue}€</p>
              <p className="text-purple-400 text-xs">{stats.week_bookings} réservations</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-500/30">
            <CardContent className="p-4">
              <p className="text-green-300 text-sm font-semibold mb-1">Confirmées / En attente</p>
              <p className="text-white text-3xl font-bold orbitron-font">
                {stats.today_confirmed} / {stats.today_pending}
              </p>
              <p className="text-green-400 text-xs">Aujourd'hui</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Today's Planning */}
      <div>
        <h2 className="text-2xl text-white orbitron-font mb-4 flex items-center gap-2">
          📅 Planning Aujourd'hui
          <span className="text-cyan-400 text-lg">
            ({new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })})
          </span>
        </h2>
        <div className="space-y-3">
          {todayBookings.length === 0 ? (
            <Card className="bg-gray-800 border-gray-600">
              <CardContent className="p-8 text-center">
                <p className="text-gray-400">🎉 Aucune réservation aujourd'hui</p>
              </CardContent>
            </Card>
          ) : (
            todayBookings.map(renderBookingCard)
          )}
        </div>
      </div>

      {/* Tomorrow's Planning */}
      <div>
        <h2 className="text-2xl text-white orbitron-font mb-4 flex items-center gap-2">
          🌅 Planning Demain
          <span className="text-purple-400 text-lg">
            ({new Date(Date.now() + 86400000).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })})
          </span>
        </h2>
        <div className="space-y-3">
          {tomorrowBookings.length === 0 ? (
            <Card className="bg-gray-800 border-gray-600">
              <CardContent className="p-8 text-center">
                <p className="text-gray-400">Aucune réservation demain</p>
              </CardContent>
            </Card>
          ) : (
            tomorrowBookings.map(renderBookingCard)
          )}
        </div>
      </div>
    </div>
  );
};


// Dashboard Statistics Component
const DashboardStats = ({ authToken, t }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alertBookings, setAlertBookings] = useState([]);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  const loadAlertBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      // Filter bookings with <50% paid and pending
      const alerts = response.data.filter(b => {
        const percentPaid = (b.amount_paid / b.total_price) * 100;
        return percentPaid < 50 && b.booking_status === 'pending';
      });
      
      setAlertBookings(alerts);
    } catch (error) {
      console.error('Error loading alert bookings:', error);
    }
  }, [authToken]);

  useEffect(() => {
    loadStats();
    loadAlertBookings();
  }, [loadStats, loadAlertBookings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-white text-lg">⏳ Chargement des statistiques...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Today's Bookings */}
        <Card className="bg-gradient-to-br from-cyan-900/50 to-cyan-800/30 border-cyan-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-300 text-sm font-semibold mb-1">{t('admin_stats_today')}</p>
                <p className="text-white text-4xl font-bold orbitron-font">{stats?.today_bookings || 0}</p>
                <p className="text-cyan-400 text-xs mt-1">{t('admin_stats_bookings')}</p>
              </div>
              <div className="bg-cyan-500/20 p-4 rounded-full">
                <CalendarDays className="w-8 h-8 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-300 text-sm font-semibold mb-1">{t('admin_stats_revenue')}</p>
                <p className="text-white text-4xl font-bold orbitron-font">{stats?.total_revenue || 0}€</p>
                <p className="text-green-400 text-xs mt-1">Total</p>
              </div>
              <div className="bg-green-500/20 p-4 rounded-full">
                <span className="text-3xl">💰</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Customers */}
        <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-300 text-sm font-semibold mb-1">{t('admin_stats_customers')}</p>
                <p className="text-white text-4xl font-bold orbitron-font">{stats?.total_customers || 0}</p>
                <p className="text-purple-400 text-xs mt-1">Clients uniques</p>
              </div>
              <div className="bg-purple-500/20 p-4 rounded-full">
                <Users className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Bookings */}
        <Card className="bg-gradient-to-br from-pink-900/50 to-pink-800/30 border-pink-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-300 text-sm font-semibold mb-1">Total Réservations</p>
                <p className="text-white text-4xl font-bold orbitron-font">{stats?.total_bookings || 0}</p>
                <p className="text-pink-400 text-xs mt-1">Toutes</p>
              </div>
              <div className="bg-pink-500/20 p-4 rounded-full">
                <span className="text-3xl">📋</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirmed Bookings */}
        <Card className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 border-emerald-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-300 text-sm font-semibold mb-1">Confirmées</p>
                <p className="text-white text-4xl font-bold orbitron-font">{stats?.confirmed_bookings || 0}</p>
                <p className="text-emerald-400 text-xs mt-1">Payées</p>
              </div>
              <div className="bg-emerald-500/20 p-4 rounded-full">
                <span className="text-3xl">✅</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Bookings */}
        <Card className="bg-gradient-to-br from-orange-900/50 to-orange-800/30 border-orange-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-300 text-sm font-semibold mb-1">En Attente</p>
                <p className="text-white text-4xl font-bold orbitron-font">{stats?.pending_bookings || 0}</p>
                <p className="text-orange-400 text-xs mt-1">Non payées</p>
              </div>
              <div className="bg-orange-500/20 p-4 rounded-full">
                <span className="text-3xl">⏳</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Alerts Section */}
      {alertBookings.length > 0 && (
        <Card className="bg-red-900/20 border-red-500/50">
          <CardHeader>
            <CardTitle className="text-red-400 orbitron-font flex items-center gap-2">
              <span className="animate-pulse">⚠️</span>
              {t('admin_stats_alerts')} ({alertBookings.length})
            </CardTitle>
            <p className="text-red-300 text-sm">{t('admin_stats_alert_subtitle')}</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertBookings.slice(0, 5).map((booking, idx) => {
                const percentPaid = (booking.amount_paid / booking.total_price) * 100;
                return (
                  <div key={idx} className="bg-gray-800/50 p-3 rounded border-l-4 border-red-500 flex justify-between items-center">
                    <div>
                      <p className="text-white font-semibold">
                        {booking.room_id?.toUpperCase()} - {booking.customer_name}
                      </p>
                      <p className="text-gray-400 text-sm">
                        📅 {formatDateString(booking.booking_date)} à {booking.start_time}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="bg-red-900/50 text-red-300 px-3 py-1 rounded text-sm font-bold">
                        {Math.round(percentPaid)}%
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {booking.amount_paid}€ / {booking.total_price}€
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {alertBookings.length > 5 && (
              <p className="text-center text-gray-400 text-sm mt-4">
                +{alertBookings.length - 5} autres réservations en alerte
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions Rapides - Section supprimée (fonctionnalités non implémentées) */}
    </div>
  );
};


// Manual Booking Form Component
const ManualBookingForm = ({ authToken, t, onBookingCreated }) => {
  const [formData, setFormData] = useState({
    room_id: '',
    booking_date: null,
    start_time: '',
    duration_hours: 1,
    number_of_people: 2,
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    special_requests: '',
    paid_cash: false,
    send_email: true
  });
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Tous les créneaux possibles (pour référence)
  const allTimeSlots = [
    "13:00", "14:00", "15:00", "16:00", "17:10", "18:20", "19:30", "20:40", "21:50", "23:00"
  ];

  // Durées disponibles en fonction de l'heure (pas dépasser minuit)
  const getAvailableDurations = (startTime) => {
    if (!startTime) return [1, 2, 3, 4, 5];
    const [h, m] = startTime.split(':').map(Number);
    const minutesUntilMidnight = (24 - h) * 60 - m;
    const maxHours = Math.floor(minutesUntilMidnight / 60);
    const durations = [];
    for (let i = 1; i <= Math.min(maxHours, 5); i++) {
      durations.push(i);
    }
    return durations.length > 0 ? durations : [1];
  };

  // Charger les créneaux disponibles quand la box et la date changent
  useEffect(() => {
    const loadAvailableSlots = async () => {
      if (!formData.room_id || !formData.booking_date) {
        setAvailableTimeSlots(allTimeSlots);
        return;
      }

      setLoadingSlots(true);
      try {
        // Conversion date sans timezone UTC
        const year = formData.booking_date.getFullYear();
        const month = String(formData.booking_date.getMonth() + 1).padStart(2, '0');
        const day = String(formData.booking_date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const response = await axios.get(`${API}/rooms/${formData.room_id}/availability`, {
          params: {
            date_str: dateStr
          }
        });
        
        if (response.data.available_slots && response.data.available_slots.length > 0) {
          setAvailableTimeSlots(response.data.available_slots);
        } else {
          setAvailableTimeSlots([]);
        }
      } catch (error) {
        console.error('Error loading available slots:', error);
        // En cas d'erreur, afficher tous les créneaux
        setAvailableTimeSlots(allTimeSlots);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadAvailableSlots();
  }, [formData.room_id, formData.booking_date, formData.duration_hours]);

  // Get max capacity for selected room
  const getMaxCapacity = () => {
    if (!formData.room_id) return 20;
    return ROOMS[formData.room_id]?.capacity || 20;
  };

  // Calcul du prix automatique (prix PAR BOX, pas par personne)
  useEffect(() => {
    if (formData.room_id && formData.duration_hours && formData.number_of_people && formData.start_time) {
      const maxCapacity = ROOMS[formData.room_id]?.capacity || 1;
      const startHour = parseInt(formData.start_time.split(':')[0]);
      const isPeakHour = startHour >= 19;
      
      // Prix PAR BOX (capacité max) et PAR HEURE
      const pricePerHourPerPerson = isPeakHour ? 10 : 8;
      const pricePerHour = pricePerHourPerPerson * maxCapacity; // Prix de la box entière
      const total = pricePerHour * formData.duration_hours;
      
      setCalculatedPrice(total);
    }
  }, [formData.room_id, formData.duration_hours, formData.number_of_people, formData.start_time]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.room_id || !formData.booking_date || !formData.start_time) {
      toast({
        title: t('toast_error'),
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Conversion date sans timezone UTC
      const year = formData.booking_date.getFullYear();
      const month = String(formData.booking_date.getMonth() + 1).padStart(2, '0');
      const day = String(formData.booking_date.getDate()).padStart(2, '0');
      const bookingDateStr = `${year}-${month}-${day}`;
      
      const bookingPayload = {
        room_id: formData.room_id,
        booking_date: bookingDateStr,
        start_time: formData.start_time,
        duration_hours: formData.duration_hours,
        number_of_people: formData.number_of_people,
        total_price: calculatedPrice,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        special_requests: formData.special_requests,
        paid_cash: formData.paid_cash,
        send_email: formData.send_email,
        paid_amount: formData.paid_cash ? calculatedPrice : 0
      };

      const response = await axios.post(`${API}/admin/bookings/manual`, bookingPayload, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      toast({
        title: "✅ " + t('admin_manual_success'),
        description: `Réservation créée pour ${formData.customer_name}`,
      });

      // Reset form
      setFormData({
        room_id: '',
        booking_date: null,
        start_time: '',
        duration_hours: 1,
        number_of_people: 2,
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        special_requests: '',
        paid_cash: false,
        send_email: true
      });
      setCalculatedPrice(0);
      
      // Refresh bookings list
      if (onBookingCreated) onBookingCreated();

    } catch (error) {
      console.error('Error creating manual booking:', error);
      toast({
        title: t('toast_error'),
        description: error.response?.data?.detail || t('admin_manual_error'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedRooms = Object.entries(ROOMS).sort((a, b) => a[1].capacity - b[1].capacity);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl text-white orbitron-font mb-1">{t('admin_manual_title')}</h2>
        <p className="text-gray-400 text-sm">{t('admin_manual_subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="bg-gray-700 border-purple-500/30">
          <CardContent className="p-4 space-y-4">
            {/* Sélection Box */}
            <div>
              <label className="text-white text-sm font-semibold mb-2 block">{t('admin_manual_box')} *</label>
              <Select value={formData.room_id} onValueChange={(value) => setFormData(prev => ({ ...prev, room_id: value }))}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder={t('admin_manual_box_placeholder')} />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {sortedRooms.map(([id, room]) => (
                    <SelectItem key={id} value={id} className="text-white hover:bg-gray-700">
                      {room.name} ({room.capacity} pers.)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date et Heure */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-sm font-semibold mb-2 block">{t('admin_manual_date')} *</label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {formData.booking_date ? formData.booking_date.toLocaleDateString('fr-FR') : "Sélectionner..."}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-600">
                    <Calendar
                      mode="single"
                      selected={formData.booking_date}
                      onSelect={(date) => {
                        setFormData(prev => ({ ...prev, booking_date: date }));
                        setCalendarOpen(false);
                      }}
                      fromDate={new Date(2026, 3, 11)}
                      disabled={(date) => date < new Date(2026, 3, 11)}
                      className="text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="text-white text-sm font-semibold mb-2 block">{t('admin_manual_time')} *</label>
                <Select 
                  value={formData.start_time} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, start_time: value }))}
                  disabled={!formData.room_id || !formData.booking_date || loadingSlots}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder={loadingSlots ? "Chargement..." : t('admin_manual_time_placeholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                    {availableTimeSlots.length === 0 ? (
                      <div className="px-2 py-4 text-center text-gray-400 text-sm">
                        Aucun créneau disponible
                      </div>
                    ) : (
                      availableTimeSlots.map(time => (
                        <SelectItem key={time} value={time} className="text-white hover:bg-gray-700">
                          {time}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {!formData.room_id || !formData.booking_date ? (
                  <p className="text-xs text-gray-400 mt-1">Sélectionnez d'abord une box et une date</p>
                ) : null}
              </div>
            </div>

            {/* Durée et Personnes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-sm font-semibold mb-2 block">{t('admin_manual_duration')} *</label>
                <Select value={formData.duration_hours.toString()} onValueChange={(value) => setFormData(prev => ({ ...prev, duration_hours: parseInt(value) }))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    {getAvailableDurations(formData.start_time).map(h => (
                      <SelectItem key={h} value={h.toString()} className="text-white hover:bg-gray-700">
                        {h}h
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-white text-sm font-semibold mb-2 block">{t('admin_manual_people')} *</label>
                <Input
                  type="number"
                  min="1"
                  max={getMaxCapacity()}
                  value={formData.number_of_people}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    const maxCap = getMaxCapacity();
                    setFormData(prev => ({ ...prev, number_of_people: Math.min(val, maxCap) }));
                  }}
                  className="bg-gray-800 border-gray-600 text-white"
                />
                <p className="text-gray-400 text-xs mt-1">Max: {getMaxCapacity()} pers.</p>
              </div>
            </div>

            {/* Prix calculé */}
            {calculatedPrice > 0 && (
              <div className="bg-purple-900/20 border border-purple-500/30 rounded p-3">
                <p className="text-purple-300 text-sm font-semibold">{t('admin_manual_price')}</p>
                <p className="text-white text-2xl font-bold orbitron-font">{calculatedPrice}€</p>
              </div>
            )}

            {/* Infos Client */}
            <div className="border-t border-gray-600 pt-4 space-y-3">
              <h3 className="text-white font-semibold">Informations Client</h3>
              
              <Input
                type="text"
                placeholder={t('admin_manual_customer_name') + " *"}
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                className="bg-gray-800 border-gray-600 text-white"
                required
              />

              <Input
                type="email"
                placeholder={t('admin_manual_customer_email') + " *"}
                value={formData.customer_email}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                className="bg-gray-800 border-gray-600 text-white"
                required
              />

              <Input
                type="tel"
                placeholder={t('admin_manual_customer_phone')}
                value={formData.customer_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                className="bg-gray-800 border-gray-600 text-white"
              />

              <Textarea
                placeholder={t('admin_manual_requests')}
                value={formData.special_requests}
                onChange={(e) => setFormData(prev => ({ ...prev, special_requests: e.target.value }))}
                className="bg-gray-800 border-gray-600 text-white min-h-20"
              />
            </div>

            {/* Options */}
            <div className="space-y-2 border-t border-gray-600 pt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="paid_cash"
                  checked={formData.paid_cash}
                  onChange={(e) => setFormData(prev => ({ ...prev, paid_cash: e.target.checked }))}
                  className="w-4 h-4"
                />
                <label htmlFor="paid_cash" className="text-white text-sm cursor-pointer">
                  💵 {t('admin_manual_paid_cash')}
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bouton Submit */}
        <Button
          type="submit"
          disabled={isSubmitting || !formData.room_id || !formData.booking_date || !formData.start_time || !formData.customer_name || !formData.customer_email}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 orbitron-font font-bold text-lg py-6"
        >
          {isSubmitting ? "⏳ Création..." : `✅ ${t('admin_manual_create')}`}
        </Button>
      </form>
    </div>
  );
};

// Block Slots Manager Component - Réservation Boutique
const BlockSlotsManager = ({ authToken, t, blockedSlots, onRefresh }) => {
  const [formData, setFormData] = useState({
    room_id: '',
    blocked_date: null,
    start_time: '',
    duration_hours: 1,
    reason: 'Réservation boutique',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    block_all_day: false,
    payment_method: 'cash'  // 'cash' = en caisse, 'offered' = offert
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Vrais créneaux horaires du système
  const realTimeSlots = [
    "13:00", "14:00", "15:00", "16:00", "17:10", "18:20", "19:30", "20:40", "21:50", "23:00"
  ];

  // Charger les créneaux disponibles quand box et date changent
  useEffect(() => {
    const loadSlots = async () => {
      if (!formData.room_id || !formData.blocked_date) {
        setAvailableSlots(realTimeSlots);
        return;
      }
      setLoadingSlots(true);
      try {
        const year = formData.blocked_date.getFullYear();
        const month = String(formData.blocked_date.getMonth() + 1).padStart(2, '0');
        const day = String(formData.blocked_date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const response = await axios.get(`${API}/rooms/${formData.room_id}/availability`, {
          params: { date_str: dateStr }
        });
        if (response.data.available_slots && response.data.available_slots.length > 0) {
          setAvailableSlots(response.data.available_slots);
        } else {
          setAvailableSlots([]);
        }
      } catch (error) {
        console.error('Error loading slots:', error);
        setAvailableSlots(realTimeSlots);
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
  }, [formData.room_id, formData.blocked_date]);

  // Calculer l'heure de fin en fonction du créneau et de la durée
  const calculateEndTime = (startTime, durationHours) => {
    if (!startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    const totalMinutes = h * 60 + m + durationHours * 60;
    const endH = Math.floor(totalMinutes / 60) % 24;
    const endM = totalMinutes % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  };

  // Durées disponibles en fonction de l'heure de début (pas dépasser minuit)
  const getAvailableDurations = (startTime) => {
    if (!startTime) return [1, 2, 3, 4, 5];
    const [h, m] = startTime.split(':').map(Number);
    const minutesUntilMidnight = (24 - h) * 60 - m;
    const maxHours = Math.floor(minutesUntilMidnight / 60);
    const durations = [];
    for (let i = 1; i <= Math.min(maxHours, 5); i++) {
      durations.push(i);
    }
    return durations.length > 0 ? durations : [1];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Blocage journée entière
    if (formData.block_all_day) {
      if (!formData.blocked_date || !formData.reason) {
        toast({ title: "Erreur", description: "Veuillez sélectionner une date et une raison", variant: "destructive" });
        return;
      }
      setIsSubmitting(true);
      try {
        const year = formData.blocked_date.getFullYear();
        const month = String(formData.blocked_date.getMonth() + 1).padStart(2, '0');
        const day = String(formData.blocked_date.getDate()).padStart(2, '0');
        const blockedDateStr = `${year}-${month}-${day}`;
        const blockedBoxes = formData.room_id ? [formData.room_id] : Object.keys(ROOMS);
        for (const roomId of blockedBoxes) {
          await axios.post(`${API}/admin/blocked-slots`, {
            room_id: roomId, blocked_date: blockedDateStr, start_time: "13:00", end_time: "00:00",
            reason: formData.reason, all_day: true, booking_type: "block"
          }, { headers: { 'Authorization': `Bearer ${authToken}` } });
        }
        toast({ title: "Journée bloquée", description: `${formData.blocked_date.toLocaleDateString('fr-FR')} - Boutique fermée` });
        setFormData({ room_id: '', blocked_date: null, start_time: '', duration_hours: 1, reason: 'Réservation boutique', customer_name: '', customer_email: '', customer_phone: '', block_all_day: false, payment_method: 'cash' });
        if (onRefresh) onRefresh();
      } catch (error) {
        toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors du blocage", variant: "destructive" });
      } finally { setIsSubmitting(false); }
      return;
    }

    // Réservation boutique (créneau spécifique)
    if (!formData.room_id || !formData.blocked_date || !formData.start_time || !formData.customer_name) {
      toast({ title: "Erreur", description: "Veuillez remplir le box, la date, le créneau et le nom du client", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const year = formData.blocked_date.getFullYear();
      const month = String(formData.blocked_date.getMonth() + 1).padStart(2, '0');
      const day = String(formData.blocked_date.getDate()).padStart(2, '0');
      const blockedDateStr = `${year}-${month}-${day}`;
      const endTime = calculateEndTime(formData.start_time, formData.duration_hours);

      await axios.post(`${API}/admin/blocked-slots`, {
        room_id: formData.room_id,
        blocked_date: blockedDateStr,
        start_time: formData.start_time,
        end_time: endTime,
        reason: formData.reason || 'Réservation boutique',
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        booking_type: "walk_in",
        duration_hours: formData.duration_hours,
        payment_method: formData.payment_method
      }, { headers: { 'Authorization': `Bearer ${authToken}` } });

      const payLabel = formData.payment_method === 'offered' ? 'Offert' : 'Payé en caisse';
      toast({
        title: "Réservation boutique confirmée",
        description: `${ROOMS[formData.room_id]?.name} - ${formData.start_time} (${formData.duration_hours}h) - ${formData.customer_name} - ${payLabel}`,
      });
      setFormData({ room_id: '', blocked_date: null, start_time: '', duration_hours: 1, reason: 'Réservation boutique', customer_name: '', customer_email: '', customer_phone: '', block_all_day: false, payment_method: 'cash' });
      if (onRefresh) onRefresh();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors de la réservation", variant: "destructive" });
    } finally { setIsSubmitting(false); }
  };

  const handleUnblock = async (slotId) => {
    try {
      await axios.delete(`${API}/admin/blocked-slots/${slotId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      toast({ title: "Créneau libéré" });
      if (onRefresh) onRefresh();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors de la suppression", variant: "destructive" });
    }
  };

  const sortedRooms = Object.entries(ROOMS).sort((a, b) => a[1].capacity - b[1].capacity);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl text-white orbitron-font mb-1">Réservation Boutique / Bloquer</h2>
        <p className="text-gray-400 text-sm mb-4">Réservez un créneau pour un client sur place ou bloquez un créneau</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Card className="bg-gray-700 border-cyan-500/30">
            <CardHeader>
              <CardTitle className="text-white text-lg">Nouvelle réservation boutique</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Box */}
              <div>
                <label className="text-white text-sm font-semibold mb-2 block">Box *</label>
                <Select value={formData.room_id} onValueChange={(value) => setFormData(prev => ({ ...prev, room_id: value, start_time: '' }))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="block-room-select">
                    <SelectValue placeholder="Choisir une box" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    {sortedRooms.map(([id, room]) => (
                      <SelectItem key={id} value={id} className="text-white hover:bg-gray-700">
                        {room.name} ({room.capacity} pers.)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div>
                <label className="text-white text-sm font-semibold mb-2 block">Date *</label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left bg-gray-800 border-gray-600 text-white hover:bg-gray-700" data-testid="block-date-btn">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {formData.blocked_date ? formData.blocked_date.toLocaleDateString('fr-FR') : "Sélectionner une date..."}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-600">
                    <Calendar
                      mode="single"
                      selected={formData.blocked_date}
                      onSelect={(date) => { setFormData(prev => ({ ...prev, blocked_date: date, start_time: '' })); setCalendarOpen(false); }}
                      fromDate={new Date()}
                      className="text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Bloquer toute la journée */}
              <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input type="checkbox" checked={formData.block_all_day}
                    onChange={(e) => setFormData(prev => ({ ...prev, block_all_day: e.target.checked }))}
                    className="w-5 h-5 text-yellow-500 bg-gray-700 border-gray-600 rounded"
                    data-testid="block-all-day-checkbox" />
                  <div>
                    <span className="text-yellow-400 font-semibold text-sm">Bloquer TOUTE la journée (Fermeture boutique)</span>
                    <p className="text-gray-400 text-xs mt-1">Bloque tous les créneaux de 13h à minuit{!formData.room_id && <span className="text-yellow-300"> pour toutes les boxes</span>}</p>
                  </div>
                </label>
              </div>

              {/* Champs réservation boutique (masqués si blocage journée) */}
              {!formData.block_all_day && (
                <>
                  {/* Créneau horaire */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white text-sm font-semibold mb-2 block">Créneau *</label>
                      <Select value={formData.start_time} onValueChange={(value) => setFormData(prev => ({ ...prev, start_time: value }))}>
                        <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="block-time-select">
                          <SelectValue placeholder={loadingSlots ? "Chargement..." : "Choisir un créneau"} />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                          {availableSlots.length === 0 ? (
                            <SelectItem value="none" disabled className="text-gray-500">Aucun créneau disponible</SelectItem>
                          ) : (
                            availableSlots.map(time => (
                              <SelectItem key={time} value={time} className="text-white hover:bg-gray-700">{time}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-white text-sm font-semibold mb-2 block">Durée *</label>
                      <Select value={String(formData.duration_hours)} onValueChange={(value) => setFormData(prev => ({ ...prev, duration_hours: parseInt(value) }))}>
                        <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="block-duration-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          {getAvailableDurations(formData.start_time).map(d => (
                            <SelectItem key={d} value={String(d)} className="text-white hover:bg-gray-700">{d} heure{d > 1 ? 's' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {formData.start_time && (
                    <p className="text-cyan-400 text-sm">
                      Créneau : {formData.start_time} → {calculateEndTime(formData.start_time, formData.duration_hours)}
                    </p>
                  )}

                  {/* Infos client */}
                  <div className="border-t border-gray-600 pt-4">
                    <p className="text-cyan-300 font-semibold text-sm mb-3">Informations client</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-white text-xs mb-1 block">Nom *</label>
                        <Input type="text" placeholder="Nom du client" value={formData.customer_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                          className="bg-gray-800 border-gray-600 text-white" data-testid="block-customer-name" />
                      </div>
                      <div>
                        <label className="text-white text-xs mb-1 block">Téléphone</label>
                        <Input type="tel" placeholder="06 XX XX XX XX" value={formData.customer_phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                          className="bg-gray-800 border-gray-600 text-white" data-testid="block-customer-phone" />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="text-white text-xs mb-1 block">Email</label>
                      <Input type="email" placeholder="email@exemple.com" value={formData.customer_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                        className="bg-gray-800 border-gray-600 text-white" data-testid="block-customer-email" />
                    </div>
                  </div>

                  {/* Raison / Note */}
                  <div>
                    <label className="text-white text-xs mb-1 block">Note (optionnel)</label>
                    <Input type="text" placeholder="Ex: Anniversaire, groupe d'amis..." value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      className="bg-gray-800 border-gray-600 text-white" data-testid="block-reason" />
                  </div>

                  {/* Mode de paiement */}
                  <div className="border-t border-gray-600 pt-4">
                    <label className="text-white text-sm font-semibold mb-3 block">Paiement</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button"
                        onClick={() => setFormData(prev => ({ ...prev, payment_method: 'cash' }))}
                        data-testid="payment-cash-btn"
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.payment_method === 'cash'
                            ? 'border-green-500 bg-green-500/20 text-green-300'
                            : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500'
                        }`}>
                        <span className="text-lg font-bold block">En caisse</span>
                        <span className="text-xs opacity-75">Paiement sur place</span>
                      </button>
                      <button type="button"
                        onClick={() => setFormData(prev => ({ ...prev, payment_method: 'offered' }))}
                        data-testid="payment-offered-btn"
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.payment_method === 'offered'
                            ? 'border-green-500 bg-green-500/20 text-green-300'
                            : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500'
                        }`}>
                        <span className="text-lg font-bold block">Offert</span>
                        <span className="text-xs opacity-75">Gratuit / cadeau</span>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Raison pour blocage journée */}
              {formData.block_all_day && (
                <div>
                  <label className="text-white text-sm font-semibold mb-2 block">Raison *</label>
                  <Input type="text" placeholder="Ex: Jour férié, Maintenance..." value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    className="bg-gray-800 border-gray-600 text-white" required />
                </div>
              )}
            </CardContent>
          </Card>

          <Button type="submit" data-testid="block-submit-btn"
            disabled={
              isSubmitting || !formData.blocked_date ||
              (formData.block_all_day ? !formData.reason : (!formData.room_id || !formData.start_time || !formData.customer_name))
            }
            className={`w-full orbitron-font font-bold ${
              formData.block_all_day 
                ? 'bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900' 
                : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700'
            }`}
          >
            {isSubmitting ? "En cours..." : formData.block_all_day ? "FERMER LA BOUTIQUE CE JOUR" : "CONFIRMER LA RÉSERVATION BOUTIQUE"}
          </Button>
        </form>
      </div>

      {/* Liste des créneaux bloqués / réservations boutique */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl text-white orbitron-font">Réservations boutique & créneaux bloqués</h3>
          <Button onClick={onRefresh} size="sm" variant="outline" className="border-cyan-500 text-cyan-400">
            Rafraîchir
          </Button>
        </div>

        <div className="space-y-2">
          {!blockedSlots || blockedSlots.length === 0 ? (
            <Card className="bg-gray-700 border-gray-600">
              <CardContent className="p-8 text-center">
                <p className="text-gray-400">Aucun créneau bloqué ou réservation boutique</p>
              </CardContent>
            </Card>
          ) : (
            blockedSlots.map((slot, idx) => (
              <Card key={idx} className={`${slot.booking_type === 'walk_in' ? 'bg-gray-700 border-cyan-500/30' : 'bg-gray-700 border-red-500/30'}`}>
                <CardContent className="p-4 flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-white font-bold text-lg orbitron-font">
                        {ROOMS[slot.room_id]?.name || slot.room_id}
                      </h4>
                      <Badge className={slot.booking_type === 'walk_in' ? 'bg-cyan-500/20 text-cyan-300' : 'bg-red-500/20 text-red-300'}>
                        {slot.booking_type === 'walk_in' ? 'Boutique' : slot.all_day ? 'Fermé' : 'Bloqué'}
                      </Badge>
                      {slot.payment_method === 'cash' && (
                        <Badge className="bg-green-500/20 text-green-300">Payé en caisse</Badge>
                      )}
                      {slot.payment_method === 'offered' && (
                        <Badge className="bg-green-500/20 text-green-300">Offert</Badge>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm">
                      {formatDateString(slot.blocked_date)} • {slot.start_time} - {slot.end_time}
                    </p>
                    {slot.customer_name && (
                      <p className="text-cyan-300 text-sm mt-1">
                        {slot.customer_name} {slot.customer_phone && `• ${slot.customer_phone}`} {slot.customer_email && `• ${slot.customer_email}`}
                      </p>
                    )}
                    {slot.reason && slot.reason !== 'Réservation boutique' && (
                      <p className="text-orange-300 text-xs mt-1">{slot.reason}</p>
                    )}
                  </div>
                  <Button onClick={() => handleUnblock(slot.id)} size="sm" variant="outline"
                    className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white" data-testid={`unblock-${idx}`}>
                    Supprimer
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// CountdownTimer Component - Chronomètre pour payment_deadline
const CountdownTimer = ({ deadline, bookingStatus }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [progressPercent, setProgressPercent] = useState(100);

  useEffect(() => {
    if (!deadline || bookingStatus === 'expired' || bookingStatus === 'confirmed') {
      return;
    }

    const updateTimer = () => {
      try {
        // Parse deadline (format: "2025-12-15T20:00:00+00:00")
        const deadlineDate = new Date(deadline);
        const now = new Date();
        const diff = deadlineDate - now;

        if (diff <= 0) {
          setTimeLeft('⏰ Expiré');
          setIsExpired(true);
          setProgressPercent(0);
          return;
        }

        // Calculer le temps restant
        const totalMinutes = Math.floor(diff / 1000 / 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const seconds = Math.floor((diff / 1000) % 60);

        let timeString = '';
        if (hours > 0) {
          timeString = `${hours}h ${minutes}min ${seconds}s`;
        } else {
          timeString = `${minutes}min ${seconds}s`;
        }

        setTimeLeft(timeString);
        setIsExpired(false);

        // Calculer le pourcentage (sur 30 min max)
        const maxTime = 30 * 60 * 1000; // 30 minutes en ms
        const percent = Math.min(100, Math.max(0, (diff / maxTime) * 100));
        setProgressPercent(percent);

      } catch (error) {
        console.error('Error parsing deadline:', error);
        setTimeLeft('Format invalide');
      }
    };

    // Mise à jour initiale
    updateTimer();

    // Mise à jour chaque seconde
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [deadline, bookingStatus]);

  if (!deadline || bookingStatus === 'confirmed') {
    return null;
  }

  if (bookingStatus === 'expired') {
    return (
      <div className="bg-red-900/30 border-2 border-red-500 p-4 rounded-lg">
        <div className="flex items-center gap-3">
          <span className="text-4xl">⏰</span>
          <div className="flex-1">
            <p className="text-red-400 font-bold text-lg">Réservation expirée</p>
            <p className="text-red-300 text-sm">Le délai de paiement n'a pas été respecté</p>
          </div>
        </div>
      </div>
    );
  }

  // Couleur de la barre de progression
  const colorClass = progressPercent > 50 ? 'bg-green-500' : progressPercent > 25 ? 'bg-yellow-500' : 'bg-red-500';
  
  // Couleur du fond : Orange en cours, Rouge si délai dépassé
  const bgColorClass = isExpired ? 'bg-red-900/30 border-red-500' : 'bg-orange-900/30 border-orange-500';
  const textColorClass = isExpired ? 'text-red-400' : 'text-orange-400';

  return (
    <div className={`${bgColorClass} border-2 p-4 rounded-lg`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-4xl">⏱️</span>
        <div className="flex-1">
          <p className={`${textColorClass} font-bold text-lg`}>
            {isExpired ? '⏰ Délai dépassé - En attente d\'expiration automatique' : `Temps restant : ${timeLeft}`}
          </p>
          <p className="text-gray-400 text-sm">
            {isExpired 
              ? 'Le système va expirer cette réservation dans les 5 prochaines minutes'
              : 'Paiement minimum requis : 50% du total'
            }
          </p>
        </div>
      </div>
      
      {/* Barre de progression */}
      <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
        <div 
          className={`${colorClass} h-full transition-all duration-1000 ease-linear`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      {progressPercent < 25 && !isExpired && (
        <p className="text-red-400 text-xs mt-2 font-semibold animate-pulse">
          ⚠️ URGENT : Moins de {Math.ceil((progressPercent / 100) * 30)} minutes restantes !
        </p>
      )}
    </div>
  );
};

// ContactMessageCard Component - Composant séparé pour éviter les hooks dans le map
const ContactMessageCard = ({ contact, authToken, userRole, t, onRefresh }) => {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState(contact.admin_comment || '');
  const isTreated = contact.treated || false;

  return (
    <Card className={`bg-gray-700/50 ${isTreated ? 'border-green-500' : 'border-red-500'} border-l-4`}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="text-white font-semibold orbitron-font">{contact.name}</h4>
          <span className="text-xs text-gray-400">
            {new Date(contact.created_at).toLocaleString('fr-FR')}
          </span>
        </div>
        <p className="text-cyan-400 text-sm mb-1">📧 {contact.email}</p>
        {contact.phone && <p className="text-purple-400 text-sm mb-2">📱 {contact.phone}</p>}
        <p className="text-gray-300 text-sm bg-gray-800/50 p-3 rounded border-l-2 border-pink-500 mb-3">
          {contact.message}
        </p>

        {/* Admin Comment Section */}
        {contact.admin_comment && (
          <div className="bg-blue-900/20 p-3 rounded border border-blue-500/30 mb-3">
            <p className="text-blue-300 text-xs font-semibold mb-1">💬 Commentaire interne :</p>
            <p className="text-blue-200 text-sm">{contact.admin_comment}</p>
            {contact.comment_by && (
              <p className="text-blue-400 text-xs mt-1">
                Par {contact.comment_by} - {contact.comment_updated_at ? new Date(contact.comment_updated_at).toLocaleString('fr-FR') : ''}
              </p>
            )}
          </div>
        )}

        {/* Comment Input */}
        {showCommentInput && (
          <div className="mb-3 flex gap-2">
            <Textarea
              placeholder="Ajouter un commentaire interne..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="bg-gray-800 border-gray-600 text-white min-h-20"
            />
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    await axios.patch(`${API}/admin/contacts/${contact.id}/comment`,
                      { comment: commentText },
                      { headers: { 'Authorization': `Bearer ${authToken}` }}
                    );
                    toast({ title: "Commentaire ajouté" });
                    setShowCommentInput(false);
                    onRefresh();
                  } catch (error) {
                    console.error('Error adding comment:', error);
                    toast({ title: "Erreur", variant: "destructive" });
                  }
                }}
                className="bg-blue-500 hover:bg-blue-600"
              >
                ✅
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCommentInput(false)}
              >
                ✖️
              </Button>
            </div>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={async () => {
              try {
                await axios.patch(`${API}/admin/contacts/${contact.id}/treated`, 
                  { treated: !isTreated },
                  { headers: { 'Authorization': `Bearer ${authToken}` }}
                );
                toast({
                  title: isTreated ? "Message marqué non traité" : "Message marqué traité",
                });
                onRefresh();
              } catch (error) {
                console.error('Error updating contact:', error);
                toast({ 
                  title: "Erreur", 
                  description: error.response?.data?.detail || "Erreur serveur",
                  variant: "destructive" 
                });
              }
            }}
            className={isTreated ? "bg-orange-500 hover:bg-orange-600" : "bg-green-500 hover:bg-green-600"}
          >
            {isTreated ? "🔄 Marquer non traité" : "✅ " + t('admin_btn_mark_treated')}
          </Button>

          <Button
            size="sm"
            onClick={() => setShowCommentInput(!showCommentInput)}
            className="bg-blue-500 hover:bg-blue-600"
          >
            💬 Commentaire
          </Button>
          
          {/* Delete button - Owner only */}
          {userRole === 'owner' && (
            <Button
              size="sm"
              variant="destructive"
              onClick={async () => {
                if (window.confirm("Êtes-vous sûr de vouloir supprimer ce message ?")) {
                  try {
                    await axios.delete(`${API}/admin/contacts/${contact.id}`, {
                      headers: { 'Authorization': `Bearer ${authToken}` }
                    });
                    toast({
                      title: "Message supprimé",
                    });
                    onRefresh();
                  } catch (error) {
                    console.error('Error deleting contact:', error);
                    toast({ 
                      title: "Erreur", 
                      description: error.response?.data?.detail || "Erreur serveur",
                      variant: "destructive" 
                    });
                  }
                }
              }}
            >
              🗑️ {t('admin_btn_delete')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Admin Login Component
const AdminLogin = () => {
  const { t } = useLanguage();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [userRole, setUserRole] = useState('owner'); // owner or employee
  const [activeTab, setActiveTab] = useState('planning'); // Default for employees
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortByDate, setSortByDate] = useState('created'); // 'created' ou 'booking'

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const role = localStorage.getItem('admin_role') || 'owner';
    if (token) {
      setAuthToken(token);
      setUserRole(role);
      setIsAuthenticated(true);
      // Set default tab based on role
      setActiveTab(role === 'employee' ? 'planning' : 'calendar');
      loadAdminData(token);
    }
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (isAuthenticated && authToken) {
      loadAdminData(authToken);
    }
  }, [activeTab, isAuthenticated, authToken]);

  // Event listener for opening booking details from calendar
  useEffect(() => {
    const handleOpenBooking = (event) => {
      setSelectedBooking(event.detail);
    };
    
    window.addEventListener('openBookingDetail', handleOpenBooking);
    
    return () => {
      window.removeEventListener('openBookingDetail', handleOpenBooking);
    };
  }, []);


  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        email: credentials.email,
        password: credentials.password
      });
      
      const { access_token, role } = response.data;
      
      // Store token and role
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_role', role);
      setAuthToken(access_token);
      setUserRole(role);
      setIsAuthenticated(true);
      
      // Set default tab based on role
      setActiveTab(role === 'employee' ? 'planning' : 'calendar');
      
      // Load admin data
      await loadAdminData(access_token);
      
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: t('toast_error'),
        description: t('toast_invalid_credentials'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    setAuthToken(null);
    setUserRole('owner');
    setIsAuthenticated(false);
    setContacts([]);
    setBookings([]);
    setCustomers([]);
    setBlockedSlots([]);
  };

  const loadAdminData = async (token) => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Toujours charger bookings, customers, contacts et blocked slots
      const [bookingsResponse, customersResponse, contactsResponse, slotsResponse] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/customers`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/blocked-slots`, { headers })
      ]);
      
      setBookings(bookingsResponse.data);
      setCustomers(customersResponse.data.customers || []);
      setContacts(contactsResponse.data);
      setBlockedSlots(slotsResponse.data.blocked_slots || []);
      
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-gray-800 border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-white text-2xl text-center orbitron-font">
              {t('admin_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder={t('admin_email_placeholder')}
                className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                value={credentials.email}
                onChange={(e) => setCredentials(prev => ({ ...prev, email: e.target.value }))}
                required
                disabled={isLoading}
              />
              <Input
                type="password"
                placeholder={t('admin_password_placeholder')}
                className="bg-gray-700 border-gray-600 text-white noto-sans-font"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                required
                disabled={isLoading}
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 orbitron-font font-bold"
                disabled={isLoading}
              >
                {isLoading ? t('booking_processing') : t('admin_login')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-white orbitron-font">🎤 {t('admin_dashboard')}</h1>
          
          {/* Search Bar */}
          <div className="flex gap-2 w-full md:w-auto">
            <Input
              type="text"
              placeholder={t('admin_search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 w-full md:w-80"
            />
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-400 text-red-400 hover:bg-red-400 hover:text-white orbitron-font whitespace-nowrap"
            >
              {t('admin_logout')}
            </Button>
          </div>
        </div>

        <Card className="bg-gray-800 border-purple-500/30">
          <CardContent className="p-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              {userRole === 'owner' ? (
                // Owner tabs: Calendrier → Réservations → Bloquer → Messages → Statistiques → Clients
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 bg-gray-700 mb-4">
                  <TabsTrigger value="calendar" className="text-white text-xs md:text-sm">📅 {t('admin_tab_calendar')}</TabsTrigger>
                  <TabsTrigger value="bookings" className="text-white text-xs md:text-sm">📋 {t('admin_tab_bookings')}</TabsTrigger>
                  <TabsTrigger value="block" className="text-white text-xs md:text-sm">🔒 {t('admin_tab_block')}</TabsTrigger>
                  <TabsTrigger value="contacts" className="text-white text-xs md:text-sm">💬 {t('admin_tab_contacts')}</TabsTrigger>
                  <TabsTrigger value="stats" className="text-white text-xs md:text-sm">📊 {t('admin_tab_stats')}</TabsTrigger>
                  <TabsTrigger value="customers" className="text-white text-xs md:text-sm">👥 {t('admin_tab_customers')}</TabsTrigger>
                </TabsList>
              ) : (
                // Staff tabs: Planning → Réservations → Bloquer → Messages
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 bg-gray-700 mb-4">
                  <TabsTrigger value="planning" className="text-white text-xs md:text-sm">🗓️ Planning</TabsTrigger>
                  <TabsTrigger value="bookings" className="text-white text-xs md:text-sm">📋 Réservations</TabsTrigger>
                  <TabsTrigger value="block" className="text-white text-xs md:text-sm">🔒 Bloquer</TabsTrigger>
                  <TabsTrigger value="contacts" className="text-white text-xs md:text-sm">💬 Messages</TabsTrigger>
                </TabsList>
              )}

              {/* ONGLET PLANNING (Employee only) */}
              {userRole === 'employee' && (
                <TabsContent value="planning">
                  <DailyPlanningView authToken={authToken} t={t} blockedSlots={blockedSlots} />
                </TabsContent>
              )}

              {/* ONGLET STATS (Owner only) */}
              {userRole === 'owner' && (
                <TabsContent value="stats">
                  <DashboardStats authToken={authToken} t={t} />
                </TabsContent>
              )}

              {/* ONGLET RÉSERVATIONS */}
              <TabsContent value="bookings" className="space-y-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <h2 className="text-2xl text-white orbitron-font">{t('admin_bookings')}</h2>
                  
                  {/* Filters */}
                  <div className="flex gap-2 flex-wrap items-center">
                    <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                      <SelectTrigger className="w-[140px] bg-gray-700 border-gray-600 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600">
                        <SelectItem value="all" className="text-white">{t('admin_filter_all')}</SelectItem>
                        <SelectItem value="paid" className="text-white">{t('admin_filter_paid')}</SelectItem>
                        <SelectItem value="partial" className="text-white">{t('admin_filter_partial')}</SelectItem>
                        <SelectItem value="unpaid" className="text-white">{t('admin_filter_unpaid')}</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={sortByDate} onValueChange={setSortByDate}>
                      <SelectTrigger className="w-[180px] bg-gray-700 border-gray-600 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600">
                        <SelectItem value="created" className="text-white">📅 Date de création</SelectItem>
                        <SelectItem value="booking" className="text-white">🎤 Date de venue</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button onClick={() => loadAdminData(authToken)} size="sm" variant="outline" className="border-cyan-500 text-cyan-400">
                      🔄 {t('admin_btn_refresh')}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {(() => {
                    // Apply search filter and date range for employees
                    let filtered = bookings.filter(b => {
                      // Employee: only show bookings for the next 7 days
                      if (userRole === 'employee') {
                        const bookingDate = new Date(b.booking_date);
                        const today = new Date();
                        const weekFromNow = new Date(Date.now() + 7 * 86400000);
                        if (bookingDate < today || bookingDate > weekFromNow) {
                          return false;
                        }
                      }
                      
                      // Apply payment filter
                      if (paymentFilter !== 'all') {
                        const percentPaid = (b.amount_paid / b.total_price) * 100;
                        if (paymentFilter === 'paid' && percentPaid < 100) return false;
                        if (paymentFilter === 'partial' && (percentPaid < 50 || percentPaid >= 100)) return false;
                        if (paymentFilter === 'unpaid' && percentPaid > 0) return false;
                      }
                      
                      // Apply search query
                      if (!searchQuery) return true;
                      const query = searchQuery.toLowerCase();
                      return (
                        b.customer_name?.toLowerCase().includes(query) ||
                        b.customer_email?.toLowerCase().includes(query) ||
                        b.room_id?.toLowerCase().includes(query) ||
                        b.id?.toLowerCase().includes(query)
                      );
                    });
                    
                    // Apply sorting
                    filtered = filtered.sort((a, b) => {
                      if (sortByDate === 'booking') {
                        // Sort by booking date (date de venue)
                        const dateA = new Date(a.booking_date + ' ' + a.start_time);
                        const dateB = new Date(b.booking_date + ' ' + b.start_time);
                        return dateB - dateA; // Plus récent en premier
                      } else {
                        // Sort by creation date (date de création/paiement)
                        const dateA = new Date(a.created_at || 0);
                        const dateB = new Date(b.created_at || 0);
                        return dateB - dateA; // Plus récent en premier
                      }
                    });
                    
                    if (filtered.length === 0) {
                      return <p className="text-center py-8 text-gray-400">Aucune réservation trouvée</p>;
                    }
                    
                    return filtered.map((booking, index) => {
                      const percentPaid = (booking.amount_paid / booking.total_price) * 100;
                      const remaining = booking.total_price - booking.amount_paid;
                      const isExpired = booking.booking_status === 'expired';
                      
                      // Calculer statusColor pour le badge de pourcentage
                      const statusColor = percentPaid >= 100 ? 'green' : percentPaid >= 50 ? 'yellow' : 'red';
                      
                      // Vérifier si le délai est dépassé (pour pending uniquement)
                      let isDeadlinePassed = false;
                      if (booking.booking_status === 'pending' && booking.payment_deadline) {
                        try {
                          const deadline = new Date(booking.payment_deadline);
                          isDeadlinePassed = deadline < new Date();
                        } catch (e) {
                          console.error('Error parsing deadline:', e);
                        }
                      }
                      
                      // Déterminer la couleur de la carte
                      let cardBorderColor;
                      if (isExpired) {
                        cardBorderColor = 'border-gray-500/30 opacity-60'; // Gris pour expirée
                      } else if (booking.booking_status === 'confirmed') {
                        cardBorderColor = 'border-green-500/50'; // Vert pour confirmée
                      } else if (booking.booking_status === 'pending') {
                        if (isDeadlinePassed) {
                          cardBorderColor = 'border-red-500/50'; // Rouge si délai dépassé
                        } else {
                          cardBorderColor = 'border-orange-500/50'; // Orange si en cours
                        }
                      } else {
                        cardBorderColor = 'border-gray-500/50'; // Par défaut
                      }
                      
                      return (
                        <Card key={booking.id || index} className={`bg-gray-700/50 border-2 ${cardBorderColor} cursor-pointer hover:bg-gray-700`} onClick={() => setSelectedBooking(booking)}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-white font-bold text-lg orbitron-font">{booking.room_id?.toUpperCase()}</h4>
                                  {isExpired && (
                                    <Badge variant="destructive" className="bg-gray-600 text-gray-300">
                                      ⏰ EXPIRÉE
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-gray-300 text-sm">
                                  📅 {formatDateString(booking.booking_date)} à {booking.start_time}
                                </p>
                                <p className="text-gray-400 text-xs">
                                  ⏱️ {booking.duration_hours}h • 👥 {booking.number_of_people} pers.
                                </p>
                                <p className="text-white mt-2">{booking.customer_name}</p>
                                <p className="text-cyan-400 text-sm">{booking.customer_email}</p>
                                {booking.created_at && (
                                  <p className="text-gray-500 text-xs mt-1">
                                    Réservation effectuée le {new Date(booking.created_at).toLocaleDateString('fr-FR')}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                {!isExpired && (
                                <div className={`px-3 py-1 rounded text-sm font-bold ${
                                  statusColor === 'red' ? 'bg-red-900/50 text-red-300' :
                                  statusColor === 'green' ? 'bg-green-900/50 text-green-300' :
                                  'bg-yellow-900/50 text-yellow-300'
                                }`}>
                                  {Math.round(percentPaid)}%
                                </div>
                                )}
                                {!isExpired && (
                                  <>
                                    <p className="text-white mt-2">{booking.amount_paid}€ / {booking.total_price}€</p>
                                    <p className="text-sm text-gray-400">Reste: {remaining.toFixed(2)}€</p>
                                  </>
                                )}
                                {isExpired && (
                                  <p className="text-gray-400 text-sm mt-2">Non payée à temps</p>
                                )}
                              </div>
                            </div>
                            {booking.special_requests && (
                              <div className="mt-2 p-2 bg-purple-900/20 rounded border border-purple-500/30">
                                <p className="text-purple-300 text-xs">💬 {booking.special_requests}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    });
                  })()}
                </div>
              </TabsContent>

              {/* ONGLET CALENDRIER - Chronologique par dates (Owner only) */}
              {userRole === 'owner' && (
              <TabsContent value="calendar" className="space-y-4">
                <ChronologicalCalendarView authToken={authToken} t={t} bookings={bookings} blockedSlots={blockedSlots} />
              </TabsContent>
              )}

              {/* ONGLET CLIENTS (Owner only) */}
              {userRole === 'owner' && (
              <TabsContent value="customers" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl text-white orbitron-font">👥 Base de Données Clients</h2>
                  <div className="flex gap-2">
                    <Button onClick={() => loadAdminData(authToken)} size="sm">Actualiser</Button>
                    <Button 
                      onClick={() => {
                        // Export Excel simple via CSV
                        const csv = [
                          ['Nom', 'Email', 'Téléphone', 'Visites', 'Total Dépensé', 'Dernière Visite'].join(','),
                          ...customers.map(c => [
                            c.name,
                            c.email,
                            c.phone || '',
                            c.total_visits,
                            c.total_spent,
                            c.last_visit
                          ].join(','))
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'clients_popbox.csv';
                        a.click();
                      }}
                      size="sm"
                      className="bg-green-500"
                    >
                      📥 Export Excel
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-700">
                        <TableHead className="text-white">Nom</TableHead>
                        <TableHead className="text-white">Email</TableHead>
                        <TableHead className="text-white">Téléphone</TableHead>
                        <TableHead className="text-white">Visites</TableHead>
                        <TableHead className="text-white">Total Dépensé</TableHead>
                        <TableHead className="text-white">Dernière Visite</TableHead>
                        <TableHead className="text-white text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        // Apply search filter to customers
                        const filtered = customers.filter(c => {
                          if (!searchQuery) return true;
                          const query = searchQuery.toLowerCase();
                          return (
                            c.name?.toLowerCase().includes(query) ||
                            c.email?.toLowerCase().includes(query) ||
                            c.phone?.toLowerCase().includes(query)
                          );
                        });
                        
                        if (filtered.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                                Aucun client trouvé
                              </TableCell>
                            </TableRow>
                          );
                        }
                        
                        return filtered.map((customer, idx) => (
                          <TableRow key={idx} className="border-gray-700">
                            <TableCell className="text-white">{customer.name}</TableCell>
                            <TableCell className="text-cyan-400">{customer.email}</TableCell>
                            <TableCell className="text-gray-300">{customer.phone || '-'}</TableCell>
                            <TableCell className="text-white">{customer.total_visits}</TableCell>
                            <TableCell className="text-green-400 font-bold">{customer.total_spent}€</TableCell>
                            <TableCell className="text-gray-400 text-sm">
                              {customer.last_visit ? new Date(customer.last_visit).toLocaleDateString('fr-FR') : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  if (window.confirm(`Êtes-vous sûr de vouloir supprimer le client ${customer.name} (${customer.email}) ?\n\nCette action est irréversible.`)) {
                                    try {
                                      await axios.delete(`${API}/admin/customers/${customer.email}`, {
                                        headers: { 'Authorization': `Bearer ${authToken}` }
                                      });
                                      toast({
                                        title: "Client supprimé",
                                        description: `${customer.name} a été supprimé de la base de données`
                                      });
                                      loadAdminData(authToken);
                                    } catch (error) {
                                      console.error('Error deleting customer:', error);
                                      toast({
                                        title: "Erreur",
                                        description: error.response?.data?.detail || "Impossible de supprimer le client",
                                        variant: "destructive"
                                      });
                                    }
                                  }
                                }}
                              >
                                🗑️
                              </Button>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              )}

              {/* ONGLET BLOCAGE */}
              <TabsContent value="block">
                <BlockSlotsManager authToken={authToken} t={t} blockedSlots={blockedSlots} onRefresh={() => loadAdminData(authToken)} />
              </TabsContent>

              {/* ONGLET MESSAGES (Staff & Owner) */}
              <TabsContent value="contacts" className="space-y-4">
                <div>
                  <h2 className="text-2xl text-white orbitron-font">{t('admin_messages')}</h2>
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {contacts.length === 0 ? (
                    <p className="text-center py-8 text-gray-400">Aucun message</p>
                  ) : (
                    contacts.map((contact, index) => (
                      <ContactMessageCard 
                        key={contact.id || index}
                        contact={contact}
                        authToken={authToken}
                        userRole={userRole}
                        t={t}
                        onRefresh={() => loadAdminData(authToken)}
                      />
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Dialog détails réservation */}
        {selectedBooking && (
          <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
            <DialogContent className="bg-gray-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-pink-400 text-xl">📋 Détails Réservation</DialogTitle>
                <DialogDescription className="text-gray-400">Informations complètes de la réservation</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Informations principales */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Box</p>
                    <p className="text-white font-bold text-lg">{selectedBooking.room_id?.toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Date & Heure</p>
                    <p className="text-white">{formatDateString(selectedBooking.booking_date)}</p>
                    <p className="text-white">{selectedBooking.start_time} ({selectedBooking.duration_hours}h)</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Client</p>
                    <p className="text-white font-semibold">{selectedBooking.customer_name}</p>
                    <p className="text-cyan-400 text-sm">{selectedBooking.customer_email}</p>
                    <p className="text-purple-400 text-sm">{selectedBooking.customer_phone}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Personnes</p>
                    <p className="text-white font-bold text-lg">{selectedBooking.number_of_people}</p>
                  </div>
                </div>

                {/* Paiement */}
                <div className="bg-gray-700 p-4 rounded">
                  <p className="text-gray-400 text-sm mb-2">💰 Paiement</p>
                  <div className="flex justify-between items-center">
                    <span className="text-white">Total:</span>
                    <span className="text-white font-bold">{selectedBooking.total_price}€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-400">Payé:</span>
                    <span className="text-green-400 font-bold">{selectedBooking.amount_paid}€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-orange-400">Reste:</span>
                    <span className="text-orange-400 font-bold">{(selectedBooking.total_price - selectedBooking.amount_paid).toFixed(2)}€</span>
                  </div>
                </div>

                {/* Chronomètre pour réservations pending */}
                {selectedBooking.booking_status === 'pending' || selectedBooking.booking_status === 'expired' ? (
                  <CountdownTimer 
                    deadline={selectedBooking.payment_deadline} 
                    bookingStatus={selectedBooking.booking_status}
                  />
                ) : null}

                {/* Historique des Paiements */}
                {selectedBooking.payments && selectedBooking.payments.length > 0 && (
                  <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 p-4 rounded border border-purple-500/30">
                    <p className="text-purple-300 font-semibold mb-3">📜 Historique des Paiements ({selectedBooking.payments.length})</p>
                    <div className="space-y-2">
                      {selectedBooking.payments.map((payment, idx) => (
                        <div key={idx} className="bg-gray-800/50 p-3 rounded border border-gray-700 hover:border-purple-500/50 transition-colors">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-white font-semibold">{payment.payer_name}</p>
                              <p className="text-cyan-400 text-sm">{payment.payer_email}</p>
                              {payment.payer_phone && (
                                <p className="text-gray-400 text-sm">{payment.payer_phone}</p>
                              )}
                              <p className="text-gray-400 text-xs mt-1">
                                {new Date(payment.payment_date).toLocaleDateString('fr-FR')} à {new Date(payment.payment_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-green-400 font-bold text-lg">{payment.amount_paid}€</p>
                              <p className="text-xs text-green-300">{payment.payment_status}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Demandes spéciales */}
                {selectedBooking.special_requests && (
                  <div className="bg-purple-900/20 p-3 rounded border border-purple-500/30">
                    <p className="text-gray-400 text-sm mb-1">💬 Demande spéciale</p>
                    <p className="text-purple-200">{selectedBooking.special_requests}</p>
                  </div>
                )}

                {/* Notes Internes (Staff & Owner) */}
                <div className="bg-gray-700/50 p-4 rounded border border-cyan-500/30">
                  <h3 className="text-cyan-400 font-semibold mb-3">{t('admin_notes_title')}</h3>
                  
                  {/* Email d'annulation si expirée */}
                  {selectedBooking.booking_status === 'expired' && (
                    <div className={`mb-3 p-3 rounded ${selectedBooking.cancellation_email_sent ? 'bg-green-900/20 border border-green-500/30' : 'bg-red-900/20 border border-red-500/30'}`}>
                      {selectedBooking.cancellation_email_sent ? (
                        <>
                          <p className="text-green-400 font-semibold text-sm">✅ Email d'annulation envoyé</p>
                          {selectedBooking.cancellation_email_sent_at && (
                            <p className="text-green-300 text-xs mt-1">
                              {new Date(selectedBooking.cancellation_email_sent_at).toLocaleString('fr-FR')}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-red-400 font-semibold text-sm">❌ Email d'annulation non envoyé</p>
                          {selectedBooking.cancellation_email_error && (
                            <p className="text-red-300 text-xs mt-1">Erreur: {selectedBooking.cancellation_email_error}</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Affichage des notes existantes */}
                  <div className="space-y-2 mb-3">
                    {selectedBooking.internal_notes && selectedBooking.internal_notes.length > 0 ? (
                      selectedBooking.internal_notes.map((note, idx) => (
                        <div key={idx} className="bg-gray-800 p-2 rounded text-sm">
                          <p className="text-white">{note.text}</p>
                          <p className="text-gray-500 text-xs mt-1">
                            {note.author} - {new Date(note.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-sm">{t('admin_notes_empty')}</p>
                    )}
                  </div>

                  {/* Ajouter une note */}
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('admin_notes_placeholder')}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          try {
                            await axios.post(`${API}/admin/bookings/${selectedBooking.id}/notes`, 
                              { note: e.target.value },
                              { headers: { 'Authorization': `Bearer ${authToken}` }}
                            );
                            toast({ title: "Note ajoutée" });
                            e.target.value = '';
                            // Refresh booking data
                            const response = await axios.get(`${API}/admin/bookings`, {
                              headers: { 'Authorization': `Bearer ${authToken}` }
                            });
                            setBookings(response.data);
                            const updated = response.data.find(b => b.id === selectedBooking.id);
                            if (updated) setSelectedBooking(updated);
                          } catch (error) {
                            console.error('Error adding note:', error);
                          }
                        }
                      }}
                      className="bg-gray-800 border-gray-600 text-white flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={async () => {
                        const input = document.querySelector('input[placeholder*="note"]');
                        if (input && input.value.trim()) {
                          try {
                            await axios.post(`${API}/admin/bookings/${selectedBooking.id}/notes`, 
                              { note: input.value },
                              { headers: { 'Authorization': `Bearer ${authToken}` }}
                            );
                            toast({ title: "Note ajoutée" });
                            input.value = '';
                            const response = await axios.get(`${API}/admin/bookings`, {
                              headers: { 'Authorization': `Bearer ${authToken}` }
                            });
                            setBookings(response.data);
                            const updated = response.data.find(b => b.id === selectedBooking.id);
                            if (updated) setSelectedBooking(updated);
                          } catch (error) {
                            console.error('Error adding note:', error);
                          }
                        }
                      }}
                      className="bg-cyan-500 hover:bg-cyan-600"
                    >
                      {t('admin_notes_add')}
                    </Button>
                  </div>
                </div>

                {/* Actions Buttons */}
                <div className="flex gap-2 pt-4 border-t border-gray-700">
                  {/* Suppression */}
                  {/* Bouton Annuler et Envoyer Email - Visible seulement pour pending avec deadline passé */}
                  {selectedBooking.booking_status === 'pending' && (() => {
                    try {
                      if (selectedBooking.payment_deadline) {
                        const deadline = new Date(selectedBooking.payment_deadline);
                        const now = new Date();
                        if (deadline < now) {
                          return (
                            <Button
                              variant="outline"
                              className="bg-orange-600 hover:bg-orange-700 text-white border-orange-500"
                              onClick={async () => {
                                if (window.confirm('Voulez-vous annuler cette réservation et envoyer l\'email d\'annulation au client ?')) {
                                  try {
                                    const response = await axios.post(
                                      `${API}/admin/bookings/${selectedBooking.id}/cancel-and-notify`,
                                      {},
                                      { headers: { 'Authorization': `Bearer ${authToken}` } }
                                    );
                                    
                                    if (response.data.success) {
                                      toast({ 
                                        title: '✅ ' + response.data.message,
                                        description: response.data.email_sent ? 'Email envoyé au client' : 'Pas d\'email envoyé'
                                      });
                                      setSelectedBooking(null);
                                      loadAdminData(authToken);
                                    } else {
                                      toast({
                                        title: response.data.message,
                                        variant: "destructive"
                                      });
                                    }
                                  } catch (error) {
                                    console.error('Cancel error:', error);
                                    toast({
                                      title: 'Erreur lors de l\'annulation',
                                      variant: "destructive"
                                    });
                                  }
                                }
                              }}
                            >
                              📧 Annuler et Envoyer Email
                            </Button>
                          );
                        }
                      }
                    } catch (e) {
                      return null;
                    }
                    return null;
                  })()}

                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (window.confirm(t('admin_delete_confirm'))) {
                        try {
                          await axios.delete(`${API}/admin/bookings/${selectedBooking.id}`, {
                            headers: { 'Authorization': `Bearer ${authToken}` }
                          });
                          toast({ title: t('admin_delete_success') });
                          setSelectedBooking(null);
                          
                          // Recharger TOUJOURS les bookings après suppression (peu importe l'onglet)
                          const bookingsResponse = await axios.get(`${API}/admin/bookings`, { 
                            headers: { 'Authorization': `Bearer ${authToken}` } 
                          });
                          setBookings(bookingsResponse.data);
                          
                        } catch (error) {
                          console.error('Delete error:', error);
                          toast({
                            title: t('admin_delete_error'),
                            variant: "destructive"
                          });
                        }
                      }
                    }}
                  >
                    🗑️ {t('admin_btn_delete')}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setSelectedBooking(null)}
                    className="ml-auto"
                  >
                    Fermer
                  </Button>
                </div>

                {/* Infos techniques */}
                <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-gray-700">
                  <p>Source: {selectedBooking.booking_source || 'online'}</p>
                  <p>Statut: {selectedBooking.booking_status}</p>
                  <p>ID: {selectedBooking.id}</p>
                  <p>Créé le: {new Date(selectedBooking.created_at).toLocaleString('fr-FR')}</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

// Main App Component
const MainApp = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [preselectedRoom, setPreselectedRoom] = useState(null);

  const handleBookingClick = (roomId = null) => {
    setPreselectedRoom(roomId);
    setShowBooking(true);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation 
        onBookingClick={() => handleBookingClick()}
        onContactClick={() => scrollToSection('contact')}
        onBirthdayClick={() => scrollToSection('anniversaire')}
      />
      
      <HeroSection onBookingClick={() => handleBookingClick()} />
      <RoomsSection onBookingClick={handleBookingClick} />
      <PricingSection />
      <BirthdaySection />
      <ContactSection />

      {/* Booking Modal */}
      <Dialog open={showBooking} onOpenChange={(open) => {
        setShowBooking(open);
        if (!open) setPreselectedRoom(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-purple-500/30">
          <DialogHeader>
            <DialogTitle className="text-white orbitron-font">{translations.fr.booking_modal_title}</DialogTitle>
            <DialogDescription className="text-gray-400">{translations.fr.booking_modal_subtitle || 'Remplissez le formulaire pour réserver votre session'}</DialogDescription>
          </DialogHeader>
          <BookingForm 
            onSuccess={() => {
              setShowBooking(false);
              setPreselectedRoom(null);
            }} 
            preselectedRoom={preselectedRoom}
          />
        </DialogContent>
      </Dialog>

      {/* Footer - Politique d'Annulation */}
      <footer className="bg-gray-950 border-t border-gray-800 mt-20">
        <div className="container mx-auto px-6 py-8">
          <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white orbitron-font mb-4 flex items-center gap-2">
              📋 Politique d'Annulation et Remboursement
            </h3>
            
            <div className="space-y-4 text-gray-300 noto-sans-font">
              {/* Réservations confirmées */}
              <div className="bg-gray-900/50 p-4 rounded border-l-4 border-purple-500">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  ✅ Réservations confirmées (≥ 50% payé)
                </h4>
                <p className="text-sm">
                  Les réservations ayant atteint au moins 50% du montant total <strong>ne sont pas remboursables</strong>. 
                  Cependant, un <strong className="text-purple-400">avoir</strong> sera accordé si vous nous prévenez 
                  <strong className="text-purple-400"> jusqu'à 24 heures avant</strong> votre créneau réservé.
                </p>
              </div>

              {/* Réservations échouées */}
              <div className="bg-gray-900/50 p-4 rounded border-l-4 border-pink-500">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  ⏰ Réservations échouées (&lt; 50% payé)
                </h4>
                <p className="text-sm">
                  Si votre réservation en ligne n'atteint pas les 50% du prix requis dans le délai imparti, 
                  vous recevrez un <strong className="text-pink-400">remboursement intégral</strong> sous 
                  <strong className="text-pink-400"> 10 jours maximum</strong>.
                </p>
              </div>

              {/* Délais de paiement */}
              <div className="bg-gray-900/50 p-4 rounded border-l-4 border-cyan-500">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  ⏱️ Délais de paiement partagé
                </h4>
                <p className="text-sm">
                  • <strong className="text-cyan-400">Heures PEAK</strong> (19h-00h) : 20 minutes pour compléter le paiement<br/>
                  • <strong className="text-cyan-400">Heures normales</strong> : 30 minutes pour compléter le paiement
                </p>
              </div>

              {/* Contact */}
              <div className="text-center pt-4 border-t border-gray-800">
                <p className="text-sm text-gray-400">
                  Pour toute question ou demande de remboursement, contactez-nous : 
                  <a href="mailto:contact@popboxkaraoke.com" className="text-purple-400 hover:text-purple-300 ml-2 font-semibold">
                    contact@popboxkaraoke.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Instagram + Copyright */}
          <div className="text-center mt-8 space-y-4">
            <a 
              href="https://www.instagram.com/popboxnice" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-pink-600 via-purple-600 to-orange-500 hover:from-pink-500 hover:via-purple-500 hover:to-orange-400 text-white font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-pink-500/20"
              data-testid="instagram-link"
            >
              <Instagram className="w-5 h-5" />
              @popboxnice
            </a>
            <p className="text-gray-500 text-sm">© 2025 Pop Box Karaoke Nice - Tous droits réservés</p>
          </div>
        </div>
      </footer>

      <Toaster />
    </div>
  );
};

// Shared Booking Payment Component
const SharedBookingPayment = () => {
  const { token } = useLocation().pathname.split('/').pop();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  const [showPayerForm, setShowPayerForm] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const shareToken = window.location.pathname.split('/').pop();
        const response = await axios.get(`${API}/bookings/shared/${shareToken}`);
        setBooking(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Réservation non trouvée");
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, []);

  const handlePayment = async (amount) => {
    // Validate payer information
    if (!payerName || !payerEmail) {
      alert('Veuillez renseigner votre nom et email avant de payer');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
        custom_amount: amount,
        payer_name: payerName,
        payer_email: payerEmail,
        payer_phone: payerPhone
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error("Erreur paiement:", err);
      alert("Erreur lors de la création du paiement. Veuillez réessayer.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-2xl">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800 border-pink-500">
          <CardContent className="p-6 text-center">
            <h2 className="text-2xl font-bold text-red-500 mb-4">Erreur</h2>
            <p className="text-white mb-4">{error}</p>
            <Button onClick={() => window.location.href = '/'} className="bg-pink-500 hover:bg-pink-600">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const remaining = booking.total_price - booking.amount_paid;
  const pricePerPerson = booking.total_price / booking.number_of_people;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 p-4">
      <div className="max-w-2xl mx-auto py-8">
        <Card className="bg-gray-800 border-pink-500">
          <CardHeader>
            <CardTitle className="text-3xl text-center text-pink-400 orbitron-font">
              🎤 Paiement Partagé
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Payer Information Form */}
            <div className="bg-gray-700 p-4 rounded-lg border-2 border-cyan-500">
              <h3 className="text-xl text-cyan-400 mb-4 orbitron-font">👤 Vos Informations</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-white text-sm font-semibold mb-1 block">
                    Nom complet *
                  </label>
                  <Input
                    type="text"
                    placeholder="Votre nom"
                    className="bg-gray-800 border-gray-600 text-white noto-sans-font"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-semibold mb-1 block">
                    Email *
                  </label>
                  <Input
                    type="email"
                    placeholder="votre.email@exemple.com"
                    className="bg-gray-800 border-gray-600 text-white noto-sans-font"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-semibold mb-1 block">
                    Téléphone (optionnel)
                  </label>
                  <Input
                    type="tel"
                    placeholder="06 XX XX XX XX"
                    className="bg-gray-800 border-gray-600 text-white noto-sans-font"
                    value={payerPhone}
                    onChange={(e) => setPayerPhone(e.target.value)}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  * Ces informations seront utilisées pour l'historique de paiement et la confirmation
                </p>
              </div>
            </div>

            {/* Booking Info */}
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="text-xl text-cyan-400 mb-2 orbitron-font">Détails Réservation</h3>
              <div className="text-white space-y-1">
                <p><strong>Box:</strong> {booking.room_id?.toUpperCase()}</p>
                <p><strong>Date:</strong> {formatDateString(booking.booking_date)}</p>
                <p><strong>Heure:</strong> {booking.start_time}</p>
                <p><strong>Personnes:</strong> {booking.number_of_people}</p>
              </div>
            </div>

            {/* Payment Info */}
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="text-xl text-pink-400 mb-2 orbitron-font">Paiement</h3>
              <div className="text-white space-y-2">
                <p className="text-2xl"><strong>Total:</strong> {booking.total_price}€</p>
                <p className="text-xl text-green-400"><strong>Déjà payé:</strong> {booking.amount_paid}€</p>
                <p className="text-2xl text-pink-400"><strong>Reste à payer:</strong> {remaining}€</p>
                <p className="text-sm text-gray-300">Prix par personne: {pricePerPerson.toFixed(2)}€</p>
              </div>
            </div>

            {/* Payment Deadline */}
            {booking.payment_deadline && (
              <div className="bg-yellow-900/30 border border-yellow-500 p-4 rounded-lg">
                <p className="text-yellow-400 text-center">
                  ⏰ Délai de paiement: {new Date(booking.payment_deadline).toLocaleString('fr-FR')}
                </p>
              </div>
            )}

            {/* Payment Buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => handlePayment(pricePerPerson)}
                className="w-full bg-pink-500 hover:bg-pink-600 text-white text-xl py-6 orbitron-font"
              >
                💳 PAYER MA PART ({pricePerPerson.toFixed(2)}€)
              </Button>
              
              <Button
                onClick={() => handlePayment(remaining)}
                className="w-full bg-green-500 hover:bg-green-600 text-white text-xl py-6 orbitron-font"
              >
                💰 PAYER LE RESTE ({remaining.toFixed(2)}€)
              </Button>

              {/* Custom Amount */}
              <div className="bg-gray-700/50 p-4 rounded-lg border border-cyan-500/30">
                <label className="text-cyan-400 text-sm font-semibold mb-2 block">
                  💰 Ou entrez un montant personnalisé :
                </label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="1"
                    max={remaining}
                    step="0.01"
                    placeholder={`Entre 1 et ${remaining}€`}
                    className="bg-gray-700 border-gray-600 text-white text-lg noto-sans-font"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                  />
                  <Button
                    onClick={() => {
                      const amount = parseFloat(customAmount);
                      if (amount > 0 && amount <= remaining) {
                        handlePayment(amount);
                      } else {
                        alert(`Veuillez entrer un montant entre 1€ et ${remaining}€`);
                      }
                    }}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white orbitron-font whitespace-nowrap px-6"
                    disabled={!customAmount}
                  >
                    PAYER
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Entrez le montant que vous souhaitez payer
                </p>
              </div>
            </div>

            {/* Info */}
            <div className="text-center text-gray-400 text-sm">
              <p>Paiement sécurisé par Stripe</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// App Router
function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainApp />} />
          <Route path="/booking-success" element={<PaymentStatus />} />
          <Route path="/booking-cancel" element={<MainApp />} />
          <Route path="/booking/shared/:token" element={<SharedBookingPayment />} />
          <Route path="/admin" element={<AdminLogin />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;